import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import config from '../config';
import {
  Settings,
  Download,
  Play,
  Pause,
  Calendar,
  TrendingUp,
  TrendingDown,
  Activity,
  X,
  Clock,
  ChevronLeft,
  ChevronRight,
  Tag,
  Tags,
  Search,
  Filter,
  Edit3,
  Save,
  Trash2,
  Store,
  Layers
} from 'lucide-react';
import AucunCompteGoogleAds from './connectors/AucunCompteGoogleAds';
import { aujourdhuiIso } from '../utils/periodUtils';
import { adresseDeVignette } from '../utils/adresseDeVignette';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const API_URL = config.API_URL;

// Composant modal memoïsé avec Portal pour éviter les re-renders et latence
const BulkAssignModal = memo(({
  isOpen,
  onClose,
  productIds,
  setProductIds,
  tag,
  setTag,
  loading,
  onSubmit,
  suggestions = []
}) => {
  if (!isOpen) return null;

  // Convertir Set en Array si nécessaire
  const suggestionsList = Array.isArray(suggestions) ? suggestions : Array.from(suggestions);

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '500px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>Assigner un libellé en masse</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px'
            }}
          >
            <X size={20} />
          </button>
        </div>
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Product IDs (un par ligne ou séparés par virgule)
            </label>
            <textarea
              value={productIds}
              onChange={(e) => setProductIds(e.target.value)}
              placeholder={"Collez vos Product IDs ici...\nEx: product_123\nproduct_456\nproduct_789"}
              style={{
                width: '100%',
                minHeight: '200px',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                fontFamily: 'monospace',
                fontSize: '13px',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Libellé à assigner
            </label>
            <input
              type="text"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="Ex: Promo Été 2024"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
            {/* Suggestions de libellés existants */}
            {suggestionsList.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <span style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '8px', display: 'block' }}>
                  Libellés existants :
                </span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {suggestionsList.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setTag(suggestion)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        backgroundColor: tag === suggestion ? '#1a1a1a' : '#f1f5f9',
                        color: tag === suggestion ? 'white' : '#475569',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          padding: '16px 20px',
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            Annuler
          </button>
          <button
            onClick={onSubmit}
            disabled={loading || !productIds.trim() || !tag.trim()}
            style={{
              backgroundColor: '#1a1a1a',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              cursor: loading || !productIds.trim() || !tag.trim() ? 'not-allowed' : 'pointer',
              opacity: loading || !productIds.trim() || !tag.trim() ? 0.6 : 1,
              fontSize: '0.875rem'
            }}
          >
            {loading ? 'Assignation...' : 'Ajouter le libellé'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
});

BulkAssignModal.displayName = 'BulkAssignModal';

/*
 * Le bouton de tête de la barre d'étiquetage manuel.
 *
 * ⚠️ Un seul objet pour les deux, parce qu'ils se **substituent** l'un à l'autre à la même place :
 * « Libeller N produits » quand des produits sont cochés, « Assigner en masse » sinon. Deux styles
 * recopiés divergeraient au premier ajustement, et la substitution se verrait — c'est ce qui
 * arrivait quand « Libeller » portait `.btn-bulk-tag`, dont la taille de police n'était pas celle
 * de son voisin.
 */
const STYLE_BOUTON_PRINCIPAL = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '0.5rem 1rem',
  borderRadius: '0.375rem',
  fontSize: '0.875rem',
  fontWeight: '500',
  backgroundColor: '#1a1a1a',
  color: 'white',
  border: 'none',
  flexShrink: 0,
  whiteSpace: 'nowrap'
};


/**
 * `readOnly` : consultation seule, pour un accès client à qui l'agence a ouvert l'outil.
 *
 * ⚠️ **Deux barrières, et il en faut deux.** Les affordances disparaissent — réglages,
 * étiquetage manuel, gestes de masse, génération du flux — **et** les neuf fonctions
 * d'écriture sortent tôt. Masquer seul ne suffit pas : une touche du clavier ou un chemin de
 * rendu oublié atteindraient encore la route. Le serveur tranche en dernier, mais un 403 en
 * bandeau rouge se lit comme une panne alors que rien n'est cassé.
 *
 * ⚠️ **`handleAnalyze` reste ouverte**, et c'est délibéré : `POST /analyze` n'écrit rien — son
 * verbe dit « écriture », son corps n'en fait aucune. C'est elle qui rend la liste des
 * produits et leurs étiquettes, et aucune route GET ne la remplace : la fermer rendrait
 * l'outil entièrement vide pour un client. Le serveur l'exempte par la même raison, et au
 * même endroit nommé (`LECTURES_EN_POST`).
 */
const ProductLabelingTool = ({ token, selectedClientId, updateUrlWithAccount, globalConnectors = [], setCurrentView, readOnly = false }) => {
  const [accounts, setAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]); // Comptes non filtrés
  const [selectedAccount, setSelectedAccount] = useState('');
  const [feedLabels, setFeedLabels] = useState([]);
  const [selectedFeedLabel, setSelectedFeedLabel] = useState('');
  const [loadingFeedLabels, setLoadingFeedLabels] = useState(false);
  const [savedFeedLabel, setSavedFeedLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [hasAutoAnalyzed, setHasAutoAnalyzed] = useState(false);

  // États pour la vérification Merchant Center
  const [checkingMerchantCenter, setCheckingMerchantCenter] = useState(false);
  const [showCheckingMessage, setShowCheckingMessage] = useState(false); // Afficher le message seulement après un délai
  const [merchantCenterMissing, setMerchantCenterMissing] = useState(false);
  const [suggestedMerchantIds, setSuggestedMerchantIds] = useState([]);
  // Cache des comptes déjà vérifiés (pour éviter de re-vérifier et afficher le message)
  const verifiedMerchantAccountsRef = React.useRef(new Set());
  const [pendingAccountId, setPendingAccountId] = useState(null); // Compte en attente de validation Merchant Center
  const checkingTimeoutRef = React.useRef(null);
  const [configLoaded, setConfigLoaded] = useState(false); // Indique si la config a été chargée
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  
  // États pour pagination et tri
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  
  // État pour le filtre par catégorie
  const [selectedCategory, setSelectedCategory] = useState(null);
  // État pour le filtre par catégorie dans l'étiquetage manuel
  const [selectedManualCategory, setSelectedManualCategory] = useState('');
  // État pour le filtre par libellé dans l'étiquetage manuel
  const [selectedManualLabel, setSelectedManualLabel] = useState('');
  
  // État pour l'affichage de la configuration
  const [showConfig, setShowConfig] = useState(false);
  
  // État pour l'affichage de la gestion des flux XML
  const [showXmlFeeds, setShowXmlFeeds] = useState(false);
  
  // États pour le système d'onglets
  const [activeTab, setActiveTab] = useState('automatic'); // 'automatic', 'group' ou 'manual'

  // États pour l'analyse par groupe (item_group_id)
  const [groupAnalysisData, setGroupAnalysisData] = useState(null);
  const [groupsUnavailableReason, setGroupsUnavailableReason] = useState(null);
  const [groupError, setGroupError] = useState(null);
  const [groupFilter, setGroupFilter] = useState(''); // Filtre par catégorie (TOP, MIDDLE, etc.)
  const [groupSortConfig, setGroupSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [groupCurrentPage, setGroupCurrentPage] = useState(1);
  const [groupItemsPerPage] = useState(20);
  const [showProductsWithoutGroupModal, setShowProductsWithoutGroupModal] = useState(false);
  const [selectedGroupForModal, setSelectedGroupForModal] = useState(null);
  const [groupProductsSortConfig, setGroupProductsSortConfig] = useState({ key: 'cost', direction: 'desc' });

  // États pour l'étiquetage manuel
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [filteringAccounts, setFilteringAccounts] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('LAST_30_DAYS');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [bulkTag, setBulkTag] = useState('');
  const [showTagModal, setShowTagModal] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [editingTag, setEditingTag] = useState('');
  const [hoveredImage, setHoveredImage] = useState(null);
  const [tagSuggestions, setTagSuggestions] = useState(new Set());
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [showEditTagSuggestions, setShowEditTagSuggestions] = useState(false);
  const [imageTooltipPosition, setImageTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipTimeout, setTooltipTimeout] = useState(null);

  // États pour la pagination des produits manuels
  const [manualCurrentPage, setManualCurrentPage] = useState(1);
  const [manualItemsPerPage] = useState(100);

  // États pour l'assignation en masse par IDs
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkProductIds, setBulkProductIds] = useState('');
  const [bulkAssignTag, setBulkAssignTag] = useState('');
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);

  // États pour la gestion des tags (modifier/supprimer)
  const [showManageTagsModal, setShowManageTagsModal] = useState(false);
  const [editingTagName, setEditingTagName] = useState(null);
  const [newTagName, setNewTagName] = useState('');
  const [manageTagsLoading, setManageTagsLoading] = useState(false);

  // État pour savoir si la configuration a été sauvegardée
  const [isConfigSaved, setIsConfigSaved] = useState(false);

  // État pour les statuts des custom_labels (libre, utilisé, utilisé par Kokpeet)
  const [customLabelStatuses, setCustomLabelStatuses] = useState({});
  const [loadingLabelStatuses, setLoadingLabelStatuses] = useState(false);

  // États pour la mise à jour et le journal
  const [updateLogs, setUpdateLogs] = useState([]);
  const [logsCurrentPage, setLogsCurrentPage] = useState(1);
  const [logsPerPage] = useState(10);
  const [totalLogs, setTotalLogs] = useState(0);
  const [updateTime, setUpdateTime] = useState('04:00');
  const [timezone, setTimezone] = useState('Europe/Paris');
  const [chartTooltip, setChartTooltip] = useState({ visible: false, data: null, position: { x: 0, y: 0 } });
  const [activeXmlTab, setActiveXmlTab] = useState('configuration');
  
  // Configuration des seuils
  const [config, setConfig] = useState({
    analysePeriodDays: 15,
    topSalesThreshold: 10,
    topRoasThreshold: 300,
    middleRoasThreshold: 200,
    lowRoasThreshold: 100,
    updateFrequencyHours: 24,
    customLabel: '',
    manualCustomLabel: '',
    groupCustomLabel: ''
  });

  // Vérifier si les paramètres de seuils sont personnalisés (différents des valeurs par défaut)
  const hasCustomSettings = useMemo(() => {
    // Comparer uniquement les seuils numériques personnalisables
    const defaults = {
      analysePeriodDays: 15,
      topSalesThreshold: 10,
      topRoasThreshold: 300,
      middleRoasThreshold: 200,
      lowRoasThreshold: 100
    };

    return Object.keys(defaults).some(key => {
      const defaultVal = defaults[key];
      const currentVal = config[key];
      // Comparer en tant que nombres pour éviter les problèmes de type
      return Number(defaultVal) !== Number(currentVal);
    });
  }, [config]);

  // Charger les libellés de flux pour un compte
  const loadFeedLabels = async (accountId) => {
    if (!accountId) return;
    
    try {
      setLoadingFeedLabels(true);
      console.log('🏷️ [FEED-LABELS] Chargement des libellés pour le compte:', accountId);
      
      const response = await axios.get(`${API_URL}/api/tools/product-labeling/feed-labels/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const availableLabels = response.data.feedLabels || [];
        setFeedLabels(availableLabels);
        console.log('🏷️ [FEED-LABELS] Libellés chargés:', availableLabels);
        
        // Auto-sélectionner le premier libellé disponible ou 'FR' par défaut
        if (availableLabels.length > 0) {
          const defaultFeed = availableLabels.includes('FR') ? 'FR' : availableLabels[0];
          setSelectedFeedLabel(defaultFeed);
          console.log('🏷️ [FEED-LABELS] Libellé auto-sélectionné:', defaultFeed);
        } else {
          setSelectedFeedLabel('');
        }
      } else {
        console.error('❌ [FEED-LABELS] Erreur dans la réponse:', response.data.error);
        setFeedLabels([]);
      }
    } catch (error) {
      console.error('❌ [FEED-LABELS] Erreur chargement libellés:', error);
      setFeedLabels([]);
    } finally {
      setLoadingFeedLabels(false);
    }
  };

  // Vérifier si un compte Merchant Center est associé au compte Google Ads
  const checkMerchantCenterAssociation = async (accountId) => {
    // Si le compte a déjà été vérifié avec succès, ne pas re-vérifier
    if (verifiedMerchantAccountsRef.current.has(accountId)) {
      console.log('✅ [MERCHANT-CHECK] Compte déjà vérifié (cache):', accountId);
      setMerchantCenterMissing(false);
      return true;
    }

    try {
      setCheckingMerchantCenter(true);
      setShowCheckingMessage(false);

      // Afficher le message de chargement seulement après 300ms (pour éviter le flash si la vérification est rapide)
      if (checkingTimeoutRef.current) {
        clearTimeout(checkingTimeoutRef.current);
      }
      checkingTimeoutRef.current = setTimeout(() => {
        setShowCheckingMessage(true);
      }, 300);

      console.log('🔍 [MERCHANT-CHECK] Vérification Merchant Center pour compte:', accountId);

      // Appeler l'endpoint pour récupérer les Merchant Centers liés
      const response = await axios.post(`${API_URL}/api/dashboard/linked-merchant-centers`, {
        sourceId: accountId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const linkedMerchants = response.data.linkedMerchantCenters || [];
        const alreadyInLibraryCount = response.data.alreadyInLibrary || 0; // C'est un nombre, pas un tableau

        console.log('🔍 [MERCHANT-CHECK] Merchant Centers liés:', linkedMerchants.length, 'déjà en bibliothèque:', alreadyInLibraryCount);

        // Si au moins un Merchant Center est déjà dans la bibliothèque, c'est bon
        if (alreadyInLibraryCount > 0) {
          console.log('✅ [MERCHANT-CHECK] Compte Merchant Center trouvé en bibliothèque');
          // Ajouter au cache pour éviter de re-vérifier
          verifiedMerchantAccountsRef.current.add(accountId);
          setMerchantCenterMissing(false);
          setSuggestedMerchantIds([]);
          return true;
        }

        // Sinon, on indique qu'il manque un Merchant Center
        console.log('⚠️ [MERCHANT-CHECK] Aucun Merchant Center associé dans la bibliothèque');
        setMerchantCenterMissing(true);
        // Stocker les IDs des Merchant Centers suggérés (ceux qui ne sont pas déjà en bibliothèque)
        const notInLibrary = linkedMerchants.filter(m => !m.alreadyInLibrary);
        setSuggestedMerchantIds(notInLibrary.map(m => String(m.merchantCenterId)));
        return false;
      }

      // En cas d'erreur de l'API, vérifier si on a un connecteur Merchant
      console.log('⚠️ [MERCHANT-CHECK] Erreur API, vérification du connecteur');
      return checkMerchantConnector();
    } catch (error) {
      console.error('❌ [MERCHANT-CHECK] Erreur:', error);
      // En cas d'erreur, vérifier au moins si le connecteur existe
      return checkMerchantConnector();
    } finally {
      // Nettoyer le timeout et masquer le message
      if (checkingTimeoutRef.current) {
        clearTimeout(checkingTimeoutRef.current);
        checkingTimeoutRef.current = null;
      }
      setShowCheckingMessage(false);
      setCheckingMerchantCenter(false);
    }
  };

  // Vérifier si un connecteur Merchant API existe
  const checkMerchantConnector = () => {
    const hasMerchantConnector = globalConnectors.some(c =>
      c.provider === 'GOOGLE_MERCHANT_API'
    );

    if (!hasMerchantConnector) {
      console.log('⚠️ [MERCHANT-CHECK] Aucun connecteur Merchant API');
      setMerchantCenterMissing(true);
      return false;
    }

    return true;
  };

  // Ouvrir le modal pour ajouter un compte Merchant Center
  const handleConnectMerchantCenter = () => {
    if (setCurrentView) {
      // Utiliser window.handleMerchantCenterClick pour la cohérence
      if (window.handleMerchantCenterClick) {
        window.handleMerchantCenterClick(pendingAccountId);
      } else {
        // Fallback: naviguer vers la bibliothèque
        setCurrentView('library');
      }
    }
  };

  // Charger les logs de mise à jour pour un compte et libellé de flux
  const loadUpdateLogs = async (accountId, feedLabel, page = 1) => {
    if (!accountId || !feedLabel) return;
    
    try {
      console.log('📋 [UPDATE-LOGS] Chargement des logs pour le compte:', accountId, 'feedLabel:', feedLabel, 'page:', page);
      const response = await axios.get(`${API_URL}/api/tools/product-labeling/update-logs/${accountId}/${encodeURIComponent(feedLabel)}?page=${page}&limit=${logsPerPage}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setUpdateLogs(response.data.logs || []);
        setTotalLogs(response.data.total || 0);
        setLogsCurrentPage(page);
        console.log('📋 [UPDATE-LOGS] Logs chargés:', response.data.logs?.length || 0, 'total:', response.data.total);
      }
    } catch (error) {
      console.error('❌ [UPDATE-LOGS] Erreur chargement logs:', error);
    }
  };


  // Charger la configuration sauvegardée pour le compte et libellé de flux sélectionnés
  const loadSavedConfig = async (accountId, feedLabel) => {
    if (!accountId || !feedLabel) {
      console.log('⚙️ [CONFIG] Impossible de charger la config - accountId ou feedLabel manquant');
      return;
    }

    try {
      console.log('⚙️ [CONFIG] Chargement de la configuration pour le compte:', accountId, 'feedLabel:', feedLabel);
      const response = await axios.get(`${API_URL}/api/tools/product-labeling/config/${accountId}/${encodeURIComponent(feedLabel)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        console.log('⚙️ [CONFIG] Configuration chargée:', response.data.config);
        setConfig(prevConfig => ({
          ...prevConfig,
          ...response.data.config
        }));

        // Vérifier si un libellé personnalisé est sauvegardé
        setIsConfigSaved(response.data.config && response.data.config.customLabel);
        setConfigLoaded(true); // Marquer la config comme chargée
        console.log('✅ [CONFIG] Config chargée, configLoaded = true');
        return response.data.hasSavedConfig;
      } else {
        // Pas de config sauvegardée, mais le chargement est terminé
        setConfigLoaded(true);
        console.log('✅ [CONFIG] Pas de config sauvegardée, configLoaded = true');
      }
    } catch (error) {
      console.warn('⚠️ [CONFIG] Erreur lors du chargement de la configuration:', error.message);
      // Même en cas d'erreur, marquer comme chargé pour ne pas bloquer
      setConfigLoaded(true);
      console.log('⚠️ [CONFIG] Erreur mais configLoaded = true');
    }
    return false;
  };

  // Charger l'heure de mise à jour pour le compte et libellé sélectionnés
  const loadUpdateTime = async (accountId, feedLabel) => {
    if (!accountId || !feedLabel) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/tools/product-labeling/update-time/${accountId}/${encodeURIComponent(feedLabel)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setUpdateTime(response.data.updateTime);
        setTimezone(response.data.timezone || 'Europe/Paris');
      }
    } catch (error) {
      console.warn('⚠️ [UPDATE-TIME] Erreur lors du chargement de l\'heure:', error.message);
    }
  };

  // Charger le statut des custom_labels (libre, utilisé, Kokpeet)
  const loadCustomLabelStatuses = async (accountId) => {
    if (!accountId) return;

    try {
      setLoadingLabelStatuses(true);
      console.log('🏷️ [CUSTOM-LABELS-STATUS] Chargement du statut des custom_labels pour:', accountId);

      const response = await axios.get(`${API_URL}/api/tools/custom-labels/status/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data?.analysis?.labels) {
        const labels = response.data.data.analysis.labels;
        const statuses = {};

        // Transformer les données pour un accès facile
        Object.keys(labels).forEach(labelKey => {
          const labelData = labels[labelKey];
          statuses[labelKey] = {
            status: labelData.status, // 'LIBRE', 'UTILISÉ_PAR_KOKPEET', 'UTILISÉ_PAR_AUTRE_CHOSE'
            productsCount: labelData.usage?.totalProducts || 0,
            uniqueValues: labelData.usage?.uniqueValues || 0
          };
        });

        setCustomLabelStatuses(statuses);
        console.log('🏷️ [CUSTOM-LABELS-STATUS] Statuts chargés:', statuses);
      } else if (response.data.success && response.data.data?.analysis?.customLabels) {
        // Format alternatif (quand pas de Merchant Center)
        const labels = response.data.data.analysis.customLabels;
        const statuses = {};

        Object.keys(labels).forEach(labelKey => {
          statuses[labelKey] = {
            status: labels[labelKey].status || 'LIBRE',
            productsCount: labels[labelKey].productCount || 0,
            uniqueValues: labels[labelKey].uniqueValues || 0
          };
        });

        setCustomLabelStatuses(statuses);
        console.log('🏷️ [CUSTOM-LABELS-STATUS] Statuts chargés (format alt):', statuses);
      }
    } catch (error) {
      console.warn('⚠️ [CUSTOM-LABELS-STATUS] Erreur lors du chargement des statuts:', error.message);
      // Initialiser avec des valeurs par défaut en cas d'erreur
      setCustomLabelStatuses({
        custom_label_0: { status: 'INCONNU', productsCount: 0, uniqueValues: 0 },
        custom_label_1: { status: 'INCONNU', productsCount: 0, uniqueValues: 0 },
        custom_label_2: { status: 'INCONNU', productsCount: 0, uniqueValues: 0 },
        custom_label_3: { status: 'INCONNU', productsCount: 0, uniqueValues: 0 },
        custom_label_4: { status: 'INCONNU', productsCount: 0, uniqueValues: 0 }
      });
    } finally {
      setLoadingLabelStatuses(false);
    }
  };

  // Helper pour formater le statut d'un custom_label
  const getLabelStatusText = (labelKey) => {
    const status = customLabelStatuses[labelKey];
    if (!status) return '';

    switch (status.status) {
      case 'LIBRE':
        return ' (libre)';
      case 'UTILISÉ_PAR_KOKPEET':
        return ' (utilisé - Kokpit)';
      case 'UTILISÉ_PAR_AUTRE_CHOSE':
        return ` (utilisé - ${status.productsCount} produits)`;
      case 'INCONNU':
        return ' (statut inconnu)';
      default:
        return '';
    }
  };

  // Helper pour obtenir l'émoji selon le statut du label
  const getLabelStatusEmoji = (labelKey) => {
    const status = customLabelStatuses[labelKey];
    if (!status) return '';

    switch (status.status) {
      case 'LIBRE':
        return '🟢 ';
      case 'UTILISÉ_PAR_KOKPEET':
      case 'UTILISÉ_PAR_AUTRE_CHOSE':
        return '🔴 ';
      default:
        return '';
    }
  };

  // Préparer les données pour le graphique histogramme
  const prepareChartData = useMemo(() => {
    if (!updateLogs || updateLogs.length === 0) {
      return [];
    }

    try {
      return updateLogs.slice(0, 10).reverse().map((log, index) => {
        if (!log || !log.createdAt) {
          return {
            date: `Mise à jour ${index + 1}`,
            fullDate: 'Date inconnue',
            TOP: 0,
            MIDDLE: 0,
            LOW: 0,
            ZERO: 0,
            ZOMBIE: 0,
            total: 0
          };
        }

        let stats = {};
        try {
          stats = log.productsStats ? (typeof log.productsStats === 'string' ? JSON.parse(log.productsStats) : log.productsStats) : {};
        } catch (e) {
          stats = {};
        }
        
        const date = new Date(log.createdAt);
        const isValidDate = date instanceof Date && !isNaN(date);
        
        return {
          date: isValidDate ? date.toLocaleDateString('fr-FR', { 
            month: '2-digit', 
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }) : `Mise à jour ${index + 1}`,
          fullDate: isValidDate ? date.toLocaleString('fr-FR') : 'Date inconnue',
          TOP: Number(stats.TOP) || 0,
          MIDDLE: Number(stats.MIDDLE) || 0,
          LOW: Number(stats.LOW) || 0,
          ZERO: Number(stats.ZERO) || 0,
          ZOMBIE: Number(stats.ZOMBIE) || 0,
          total: Number(log.productsCount) || 0
        };
      });
    } catch (error) {
      console.error('Erreur lors de la préparation des données du graphique:', error);
      return [];
    }
  }, [updateLogs]);

  // Sauvegarder l'heure de mise à jour
  const saveUpdateTime = async () => {
    /* ⚠️ Consultation seule : la barrière de fond, celle que le masquage ne remplace pas. */
    if (readOnly) return;
    if (!selectedAccount || !selectedFeedLabel) return;

    try {
      const response = await axios.post(`${API_URL}/api/tools/product-labeling/update-time`, {
        accountId: selectedAccount,
        feedLabel: selectedFeedLabel,
        updateTime: updateTime
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        console.log('✅ [UPDATE-TIME] Heure sauvegardée:', updateTime);
        setTimezone(response.data.timezone || 'Europe/Paris');
        alert(`Heure de mise à jour configurée à ${updateTime} (${response.data.timezone || 'Europe/Paris'})`);
      }
    } catch (error) {
      console.error('❌ [UPDATE-TIME] Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde de l\'heure');
    }
  };

  // Sauvegarder la configuration pour le compte sélectionné
  const saveConfig = async (accountId, configToSave, silent = false) => {
    /* ⚠️ Consultation seule : la barrière de fond, celle que le masquage ne remplace pas. */
    if (readOnly) return;
    try {
      console.log('⚙️ [CONFIG] Sauvegarde de la configuration pour le compte:', accountId, 'feedLabel:', selectedFeedLabel);
      
      if (!selectedFeedLabel) {
        if (!silent) {
          alert('Veuillez sélectionner un libellé de flux avant de sauvegarder.');
        }
        return false;
      }
      
      // Le libellé personnalisé n'est pas obligatoire pour sauvegarder
      
      const response = await axios.post(`${API_URL}/api/tools/product-labeling/save-config`, {
        accountId,
        feedLabel: selectedFeedLabel,
        config: configToSave
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        console.log('✅ [CONFIG] Configuration sauvegardée avec succès');
        // Marquer la configuration comme sauvegardée
        setIsConfigSaved(true);
        return true;
      } else {
        console.error('❌ [CONFIG] Erreur dans la réponse:', response.data.error || 'Erreur inconnue');
        if (!silent) {
          alert('Erreur lors de la sauvegarde : ' + (response.data.error || 'Erreur inconnue'));
        }
        return false;
      }
    } catch (error) {
      console.error('❌ [CONFIG] Erreur lors de la sauvegarde:', error.message);
      if (!silent) {
        alert('Erreur lors de la sauvegarde : ' + error.message);
      }
      return false;
    }
  };

  // Chargement des comptes Google Ads
  useEffect(() => {
    const fetchAccounts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/library?usage=outils`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('📚 [PRODUCT-LABELING] Réponse API complète:', response.data);
        
        if (response.data.success && response.data.library) {
          const data = response.data;
          
          // Combiner les comptes Google Ads depuis différentes sources
          const googleSheetsItems = data.library?.GOOGLE_SHEETS || [];
          const googleAdsItems = data.library?.GOOGLE_ADS || [];
          
          console.log('📚 [PRODUCT-LABELING] Google Sheets items:', googleSheetsItems.length);
          console.log('📚 [PRODUCT-LABELING] Google Ads items:', googleAdsItems.length);
          
          // Filtrer pour ne garder que les comptes Google Ads
          const adsItems = [
            ...googleSheetsItems.filter(item => item.type === 'GOOGLE_ADS'),
            ...googleAdsItems.filter(item => item.type === 'GOOGLE_ADS')
          ];
          
          console.log('📚 [PRODUCT-LABELING] Comptes Google Ads filtrés:', adsItems);
          console.log('📚 [PRODUCT-LABELING] Chargé', adsItems.length, 'compte(s) Google Ads');
          setAllAccounts(adsItems); // Sauvegarder les comptes non filtrés
          // Ne pas définir setAccounts(adsItems) ici - le filtrage sera géré par l'useEffect
        } else {
          console.warn('📚 [PRODUCT-LABELING] Réponse API inattendue:', response.data);
          const library = response.data?.library || {};
          const allProviders = Object.values(library).flat();
          const googleAdsAccounts = allProviders.filter(
            item => item.type === 'GOOGLE_ADS'
          );
          console.log('📚 [PRODUCT-LABELING] Fallback - Comptes trouvés:', googleAdsAccounts);
          setAllAccounts(googleAdsAccounts); // Sauvegarder les comptes non filtrés
          // Ne pas définir setAccounts(googleAdsAccounts) ici - le filtrage sera géré par l'useEffect
        }
      } catch (err) {
        setError('Erreur lors du chargement des comptes Google Ads');
        console.error('Erreur:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [token]);

  // Filtrer les comptes selon le client sélectionné
  const filterAccountsByClient = async (accounts, clientId) => {
    if (!clientId) {
      return accounts; // Pas de filtrage si aucun client sélectionné
    }
    
    try {
      // Récupérer les sites du client pour obtenir les accountIds autorisés
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success && data.sites) {
        const clientSites = data.sites.filter(site => site.clientId === clientId);
        const authorizedAccountIds = clientSites.flatMap(site => site.accountIds || []);
        
        console.log('🎯 [FILTER PRODUCT-LABELING] Sites du client:', clientSites.length);
        console.log('🎯 [FILTER PRODUCT-LABELING] IDs comptes autorisés:', authorizedAccountIds);
        
        // Filtrer les comptes
        const filteredAccounts = accounts.filter(account => {
          const matchesId = authorizedAccountIds.includes(account.id);
          const matchesSourceId = authorizedAccountIds.includes(account.sourceId);
          const isAuthorized = matchesId || matchesSourceId;
          
          if (!isAuthorized) {
            console.log(`❌ [FILTER DEBUG] Compte non autorisé: ${account.name} (id: ${account.id}, sourceId: ${account.sourceId})`);
          } else {
            console.log(`✅ [FILTER DEBUG] Compte autorisé: ${account.name} (matchesId: ${matchesId}, matchesSourceId: ${matchesSourceId})`);
          }
          
          return isAuthorized;
        });
        
        console.log('🎯 [FILTER PRODUCT-LABELING] Comptes filtrés:', filteredAccounts.length, '/', accounts.length);
        console.log('🎯 [FILTER PRODUCT-LABELING] Premier compte pour debug:', accounts[0] ? {
          id: accounts[0].id, 
          sourceId: accounts[0].sourceId, 
          name: accounts[0].name
        } : 'Aucun compte');
        return filteredAccounts;
      }
    } catch (error) {
      console.error('❌ [FILTER PRODUCT-LABELING] Erreur filtrage comptes:', error);
    }
    
    return accounts; // Retourner tous les comptes en cas d'erreur
  };

  // Filtrer les comptes qui ont des produits
  const filterAccountsWithProducts = async (accounts) => {
    console.log('🛍️ [FILTER-PRODUCTS] Vérification des comptes avec produits...');
    const accountsWithProducts = [];
    
    for (const account of accounts) {
      try {
        console.log(`🛍️ [FILTER-PRODUCTS] Vérification compte: ${account.accountId || account.id}`);
        const response = await axios.get(`${API_URL}/api/tools/product-labeling/check-products/${account.accountId || account.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success && response.data.hasProducts) {
          accountsWithProducts.push(account);
          console.log(`✅ [FILTER-PRODUCTS] Compte ${account.accountId || account.id} a des produits (${response.data.productsCount} trouvés)`);
        } else {
          console.log(`⚠️ [FILTER-PRODUCTS] Compte ${account.accountId || account.id} n'a pas de produits (${response.data.note || 'pas de note'})`);
        }
      } catch (error) {
        console.error(`❌ [FILTER-PRODUCTS] Erreur vérification compte ${account.accountId || account.id}:`, error);
        // En cas d'erreur réseau, on garde le compte par précaution, sinon on l'exclut
        if (error.code === 'NETWORK_ERROR' || error.message.includes('timeout')) {
          console.log(`🔄 [FILTER-PRODUCTS] Erreur réseau - compte ${account.accountId || account.id} conservé par précaution`);
          accountsWithProducts.push(account);
        } else {
          console.log(`🚫 [FILTER-PRODUCTS] Compte ${account.accountId || account.id} exclu à cause de l'erreur`);
        }
      }
    }
    
    console.log(`🛍️ [FILTER-PRODUCTS] ${accountsWithProducts.length}/${accounts.length} comptes avec produits`);
    return accountsWithProducts;
  };

  // Recharger la liste des comptes quand le client sélectionné change
  useEffect(() => {
    console.log('🎯 [PRODUCT-LABELING] selectedClientId changé, filtrage des comptes...');
    const filterAccounts = async () => {
      if (allAccounts.length > 0) {
        setFilteringAccounts(true); // Commencer le filtrage
        setAccounts([]); // Vider temporairement la liste des comptes
        
        try {
          const filteredItems = await filterAccountsByClient(allAccounts, selectedClientId);
          // TEMPORAIREMENT DÉSACTIVÉ - Filtrage par produits
          // const accountsWithProducts = await filterAccountsWithProducts(filteredItems);
          // setAccounts(accountsWithProducts);
          // Trier les comptes par ordre alphabétique
          const sortedAccounts = [...filteredItems].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
          );
          setAccounts(sortedAccounts);
        } catch (error) {
          console.error('❌ [FILTER-ACCOUNTS] Erreur lors du filtrage:', error);
          // En cas d'erreur, afficher tous les comptes filtrés par client au minimum
          const filteredItems = await filterAccountsByClient(allAccounts, selectedClientId);
          // Trier les comptes par ordre alphabétique
          const sortedAccounts = [...filteredItems].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
          );
          setAccounts(sortedAccounts);
        } finally {
          setFilteringAccounts(false); // Terminer le filtrage
        }
        
        // Reset la sélection de compte et vider les données si le client change
        if (selectedAccount) {
          setSelectedAccount('');
          setResults(null);
          setFeedLabels([]);
          setSelectedFeedLabel('');
          setHasAutoAnalyzed(false); // Réinitialiser le flag d'auto-analyse
                    setGroupAnalysisData(null); // Réinitialiser les données de groupe
          setConfigLoaded(false); // Réinitialiser le flag de config chargée
          setShowConfig(false); // Fermer la fenêtre de paramètres
          setShowXmlFeeds(false); // Fermer la fenêtre XML
          console.log('🎯 [PRODUCT-LABELING] Changement de client détecté, reset de la sélection et des données');
        }
      }
    };
    
    filterAccounts();
  }, [selectedClientId, allAccounts]);

  // Sélection automatique du compte si un seul disponible (avec vérification Merchant Center)
  useEffect(() => {
    const autoSelectAccount = async () => {
      if (accounts.length === 1 && !selectedAccount && !pendingAccountId && !filteringAccounts) {
        const accountId = accounts[0].sourceId || accounts[0].id;
        console.log('🎯 [AUTO-SELECT] Un seul compte disponible, vérification Merchant Center:', accounts[0].name);

        setPendingAccountId(accountId);
        const hasMerchant = await checkMerchantCenterAssociation(accountId);

        if (hasMerchant) {
          setSelectedAccount(accountId);
          setPendingAccountId(null);
          if (updateUrlWithAccount) updateUrlWithAccount(accountId);
        }
      }
    };

    autoSelectAccount();
  }, [accounts, selectedAccount, pendingAccountId, filteringAccounts]);

  // Écouter l'événement de retour depuis la bibliothèque après ajout d'un Merchant Center
  useEffect(() => {
    const handleMerchantAdded = async () => {
      // Si on a un compte en attente, revérifier le Merchant Center
      if (pendingAccountId) {
        console.log('🔄 [MERCHANT-ADDED] Revérification du Merchant Center pour:', pendingAccountId);
        const hasMerchant = await checkMerchantCenterAssociation(pendingAccountId);

        if (hasMerchant) {
          setSelectedAccount(pendingAccountId);
          setPendingAccountId(null);
          setMerchantCenterMissing(false);
          if (updateUrlWithAccount) updateUrlWithAccount(pendingAccountId);
        }
      }
    };

    // Écouter quand on revient sur cette vue
    window.addEventListener('focus', handleMerchantAdded);

    return () => {
      window.removeEventListener('focus', handleMerchantAdded);
    };
  }, [pendingAccountId, updateUrlWithAccount]);

  // Charger les données quand le compte sélectionné change
  useEffect(() => {
    if (selectedAccount) {
      loadFeedLabels(selectedAccount); // Charger les libellés de flux
      loadCustomLabelStatuses(selectedAccount); // Charger le statut des custom_labels
      // Réinitialiser les résultats de l'analyse précédente
      setResults(null);
      setError('');
      setLogsCurrentPage(1);
      setAnalyzing(false); // Réinitialiser l'état d'analyse
      // Réinitialiser le libellé de flux sélectionné
      setSelectedFeedLabel('');
      setHasAutoAnalyzed(false); // Réinitialiser le flag d'auto-analyse
            setGroupAnalysisData(null); // Réinitialiser les données de groupe
      setConfigLoaded(false); // Réinitialiser le flag de config chargée
      setIsConfigSaved(false);
      // Réinitialiser les logs et l'heure de mise à jour
      setUpdateLogs([]);
      setTotalLogs(0);
      setUpdateTime('04:00');
      // Fermer les fenêtres ouvertes quand on change de compte
      setShowConfig(false);
      setShowXmlFeeds(false);
    } else {
      // Réinitialiser tout si aucun compte sélectionné
      setResults(null);
      setError('');
      setUpdateLogs([]);
      setTotalLogs(0);
      setLogsCurrentPage(1);
      setAnalyzing(false); // Réinitialiser l'état d'analyse
      setConfigLoaded(false); // Réinitialiser le flag de config chargée
      setIsConfigSaved(false); // Réinitialiser l'état de sauvegarde
      setFeedLabels([]);
      setSelectedFeedLabel('');
      setSavedFeedLabel('');
    }
  }, [selectedAccount]);

  // Charger la configuration quand le libellé de flux sélectionné change
  useEffect(() => {
    if (selectedAccount && selectedFeedLabel) {
      // IMPORTANT: Réinitialiser configLoaded AVANT de charger pour éviter la race condition
      setConfigLoaded(false);
      setHasAutoAnalyzed(false); // Réinitialiser le flag d'auto-analyse pour permettre une nouvelle analyse
            setGroupAnalysisData(null); // Réinitialiser les données de groupe

      loadSavedConfig(selectedAccount, selectedFeedLabel);
      loadUpdateLogs(selectedAccount, selectedFeedLabel, 1); // Commencer à la page 1
      loadUpdateTime(selectedAccount, selectedFeedLabel); // Charger l'heure de mise à jour
    } else {
      // Réinitialiser la configuration si pas de libellé sélectionné
      setConfig({
        analysePeriodDays: 15,
        topSalesThreshold: 10,
        topRoasThreshold: 300,
        middleRoasThreshold: 200,
        lowRoasThreshold: 100,
        customLabel: ''
      });
      setConfigLoaded(false); // Pas de config à charger
      setIsConfigSaved(false);
      setUpdateLogs([]);
      setTotalLogs(0);
      setUpdateTime('04:00');
      setHasAutoAnalyzed(false);
      setGroupAnalysisData(null);
    }
  }, [selectedAccount, selectedFeedLabel]);

  // Fonctions pour l'étiquetage manuel
  const loadProducts = async () => {
    if (!selectedAccount) {
      return;
    }

    try {
      setLoadingProducts(true);
      setError('');
      
      
      const params = {
        period: selectedPeriod,
        feedLabel: selectedFeedLabel || undefined
      };
      
      if (selectedPeriod === 'CUSTOM') {
        params.customStartDate = customStartDate;
        params.customEndDate = customEndDate;
      }
      
      const response = await axios.get(`${API_URL}/api/tools/product-labeling/products/${selectedAccount}`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      if (response.data.success) {
        setProducts(response.data.products);
      } else {
        throw new Error(response.data.error || 'Erreur lors du chargement des produits');
      }
      
    } catch (error) {
      console.error('❌ [LOAD-PRODUCTS] Erreur:', error);
      setError(`Erreur lors du chargement des produits: ${error.response?.data?.error || error.message}`);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const applyManualTag = useCallback(async (productId, tag) => {
    /* ⚠️ Consultation seule : la barrière de fond, celle que le masquage ne remplace pas. */
    if (readOnly) return;
    if (!tag?.trim() || !productId || !selectedAccount) return;
    
    try {
      const trimmedTag = tag.trim();
      
      // Mise à jour optimiste de l'UI d'abord (pour réactivité)
      setProducts(prev => prev.map(product => 
        product.productId === productId 
          ? { ...product, manualTag: trimmedTag }
          : product
      ));
      
      // Puis faire l'appel API
      const response = await axios.put(`${API_URL}/api/tools/product-labeling/manual-tag`, {
        accountId: selectedAccount,
        productId: productId,
        tag: trimmedTag,
        feedLabel: selectedFeedLabel
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Retarder l'ajout aux suggestions pour éviter le rechargement des produits
        setTimeout(() => {
          setTagSuggestions(prev => {
            if (prev.has(trimmedTag)) return prev;
            return new Set([...prev, trimmedTag]);
          });
        }, 300);
      } else {
        // Annuler la mise à jour optimiste en cas d'erreur
        setProducts(prev => prev.map(product => 
          product.productId === productId 
            ? { ...product, manualTag: product.originalManualTag || '' }
            : product
        ));
        throw new Error(response.data.error || 'Erreur lors de l\'application du libellé');
      }
      
    } catch (error) {
      console.error('Erreur application tag:', error);
      setError(`Erreur lors de l'application du libellé : ${error.response?.data?.error || error.message}`);
      // Annuler la mise à jour optimiste
      setProducts(prev => prev.map(product => 
        product.productId === productId 
          ? { ...product, manualTag: product.originalManualTag || '' }
          : product
      ));
    }
  }, [selectedAccount, selectedFeedLabel, token]);

  const removeManualTag = useCallback(async (productId) => {
    /* ⚠️ Consultation seule : la barrière de fond, celle que le masquage ne remplace pas. */
    if (readOnly) return;
    if (!productId || !selectedAccount || !selectedFeedLabel) {
      console.error('❌ [REMOVE-TAG] Paramètres manquants:', { productId, selectedAccount, selectedFeedLabel });
      return;
    }
    
    try {
      console.log('🗑️ [REMOVE-TAG] Suppression tag pour produit:', productId, 'compte:', selectedAccount, 'feed:', selectedFeedLabel);
      
      // Mise à jour optimiste
      setProducts(prev => prev.map(product => 
        product.productId === productId 
          ? { ...product, originalManualTag: product.manualTag, manualTag: '' }
          : product
      ));
      
      const response = await axios.delete(`${API_URL}/api/tools/product-labeling/manual-tag/${productId}?accountId=${selectedAccount}&feedLabel=${encodeURIComponent(selectedFeedLabel)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.data.success) {
        // Restaurer l'état en cas d'erreur
        setProducts(prev => prev.map(product => 
          product.productId === productId 
            ? { ...product, manualTag: product.originalManualTag || '' }
            : product
        ));
        throw new Error(response.data.error || 'Erreur lors de la suppression du libellé');
      }
      
    } catch (error) {
      console.error('Erreur suppression tag:', error);
      setError(`Erreur lors de la suppression du libellé : ${error.response?.data?.error || error.message}`);
      // Restaurer l'état en cas d'erreur
      setProducts(prev => prev.map(product => 
        product.productId === productId 
          ? { ...product, manualTag: product.originalManualTag || '' }
          : product
      ));
    }
  }, [selectedAccount, selectedFeedLabel, token]);

  const applyBulkTag = useCallback(async () => {
    /* ⚠️ Consultation seule : la barrière de fond, celle que le masquage ne remplace pas. */
    if (readOnly) return;
    if (selectedProducts.size === 0 || !bulkTag.trim()) {
      setError('Veuillez sélectionner des produits et saisir un libellé');
      return;
    }

    try {
      const trimmedTag = bulkTag.trim();
      const productIdsArray = Array.from(selectedProducts);
      
      // Mise à jour optimiste
      setProducts(prev => prev.map(product => 
        selectedProducts.has(product.productId)
          ? { ...product, originalManualTag: product.manualTag, manualTag: trimmedTag }
          : product
      ));
      
      const response = await axios.put(`${API_URL}/api/tools/product-labeling/bulk-manual-tag`, {
        accountId: selectedAccount,
        productIds: productIdsArray,
        tag: trimmedTag,
        feedLabel: selectedFeedLabel
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Ajouter le tag aux suggestions directement
        setTagSuggestions(prev => {
          if (prev.has(trimmedTag)) return prev;
          return new Set([...prev, trimmedTag]);
        });
        
        // Réinitialiser la sélection
        setSelectedProducts(new Set());
        setBulkTag('');
        setShowTagModal(false);
      } else {
        // Restaurer l'état en cas d'erreur
        setProducts(prev => prev.map(product => 
          selectedProducts.has(product.productId)
            ? { ...product, manualTag: product.originalManualTag || '' }
            : product
        ));
        throw new Error(response.data.error || 'Erreur lors de l\'application du libellé en lot');
      }
      
    } catch (error) {
      console.error('Erreur application tag en lot:', error);
      setError(`Erreur lors de l'application du libellé en lot : ${error.response?.data?.error || error.message}`);
      // Restaurer l'état en cas d'erreur
      setProducts(prev => prev.map(product => 
        selectedProducts.has(product.productId)
          ? { ...product, manualTag: product.originalManualTag || '' }
          : product
      ));
    }
  }, [selectedProducts, bulkTag, selectedAccount, token]);

  // Assignation en masse par liste de Product IDs
  const handleBulkAssignByIds = useCallback(async () => {
    /* ⚠️ Consultation seule : la barrière de fond, celle que le masquage ne remplace pas. */
    if (readOnly) return;
    if (!bulkProductIds.trim() || !bulkAssignTag.trim()) return;

    setBulkAssignLoading(true);
    try {
      // Parser les IDs (séparés par virgule, retour ligne ou espace)
      const productIds = bulkProductIds
        .split(/[\n,\s]+/)
        .map(id => id.trim())
        .filter(id => id.length > 0);

      if (productIds.length === 0) {
        setError('Aucun Product ID valide trouvé');
        setBulkAssignLoading(false);
        return;
      }

      // Appel API bulk
      const response = await axios.put(
        `${API_URL}/api/tools/product-labeling/bulk-manual-tag`,
        {
          accountId: selectedAccount,
          productIds: productIds,
          tag: bulkAssignTag.trim(),
          feedLabel: selectedFeedLabel
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Mise à jour locale des produits affichés
        setProducts(prev => prev.map(product =>
          productIds.includes(product.productId)
            ? { ...product, manualTag: bulkAssignTag.trim() }
            : product
        ));

        // Ajouter aux suggestions
        setTagSuggestions(prev => new Set([...prev, bulkAssignTag.trim()]));

        // Fermer et reset
        setShowBulkAssignModal(false);
        setBulkProductIds('');
        setBulkAssignTag('');

        // Message de succès
        const updatedCount = response.data.updatedCount || productIds.length;
        alert(`Libellé « ${bulkAssignTag.trim()} » assigné à ${updatedCount} produit(s)`);
      } else {
        throw new Error(response.data.error || 'Erreur lors de l\'assignation en masse');
      }
    } catch (error) {
      console.error('Erreur assignation en masse:', error);
      setError(`Erreur lors de l'assignation en masse: ${error.response?.data?.error || error.message}`);
    } finally {
      setBulkAssignLoading(false);
    }
  }, [bulkProductIds, bulkAssignTag, selectedAccount, selectedFeedLabel, token]);

  // Callback pour fermer la modal
  const closeBulkAssignModal = useCallback(() => {
    setShowBulkAssignModal(false);
  }, []);

  // Fermer la modal de gestion des tags
  const closeManageTagsModal = useCallback(() => {
    setShowManageTagsModal(false);
    setEditingTagName(null);
    setNewTagName('');
  }, []);

  // Renommer un tag sur tous les produits
  const handleRenameTag = useCallback(async (oldTag, newTag) => {
    /* ⚠️ Consultation seule : la barrière de fond, celle que le masquage ne remplace pas. */
    if (readOnly) return;
    if (!oldTag || !newTag.trim() || oldTag === newTag.trim()) return;

    setManageTagsLoading(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/tools/product-labeling/rename-tag`,
        {
          accountId: selectedAccount,
          oldTag: oldTag,
          newTag: newTag.trim(),
          feedLabel: selectedFeedLabel
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Mise à jour locale des produits
        setProducts(prev => prev.map(product =>
          product.manualTag === oldTag
            ? { ...product, manualTag: newTag.trim() }
            : product
        ));

        // Mettre à jour les suggestions
        setTagSuggestions(prev => {
          const updated = new Set(prev);
          updated.delete(oldTag);
          updated.add(newTag.trim());
          return updated;
        });

        setEditingTagName(null);
        setNewTagName('');
      } else {
        throw new Error(response.data.error || 'Erreur lors du renommage');
      }
    } catch (error) {
      console.error('Erreur renommage tag:', error);
      alert(`Erreur: ${error.response?.data?.error || error.message}`);
    } finally {
      setManageTagsLoading(false);
    }
  }, [selectedAccount, selectedFeedLabel, token]);

  // Supprimer un tag de tous les produits
  const handleDeleteTag = useCallback(async (tagToDelete) => {
    /* ⚠️ Consultation seule : la barrière de fond, celle que le masquage ne remplace pas. */
    if (readOnly) return;
    if (!tagToDelete) return;

    const confirmDelete = window.confirm(
      `Êtes-vous sûr de vouloir supprimer le libellé « ${tagToDelete} » ?\n\nCette action supprimera ce libellé de TOUS les produits qui l'ont.`
    );

    if (!confirmDelete) return;

    setManageTagsLoading(true);
    try {
      const response = await axios.delete(
        `${API_URL}/api/tools/product-labeling/delete-tag`,
        {
          data: {
            accountId: selectedAccount,
            tag: tagToDelete,
            feedLabel: selectedFeedLabel
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        // Mise à jour locale des produits
        setProducts(prev => prev.map(product =>
          product.manualTag === tagToDelete
            ? { ...product, manualTag: null }
            : product
        ));

        // Retirer des suggestions
        setTagSuggestions(prev => {
          const updated = new Set(prev);
          updated.delete(tagToDelete);
          return updated;
        });

        alert(`Libellé « ${tagToDelete} » supprimé de ${response.data.updatedCount || 0} produit(s)`);
      } else {
        throw new Error(response.data.error || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression tag:', error);
      alert(`Erreur: ${error.response?.data?.error || error.message}`);
    } finally {
      setManageTagsLoading(false);
    }
  }, [selectedAccount, selectedFeedLabel, token]);

  // Charger les produits quand on change de période ou de compte
  useEffect(() => {
    if (activeTab === 'manual' && selectedAccount) {
      loadProducts();
    }
  }, [activeTab, selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedFeedLabel]);

  const handleAnalyze = useCallback(async (skipSave = false) => {
    console.log('🔍 [ANALYZE] Bouton cliqué', {
      selectedAccount,
      selectedFeedLabel,
      analyzing,
      hasToken: !!token,
      skipSave,
      config
    });

    if (!selectedAccount) {
      setError('Veuillez sélectionner un compte Google Ads');
      console.log('❌ [ANALYZE] Pas de compte sélectionné');
      return;
    }

    if (!selectedFeedLabel) {
      setError('Veuillez sélectionner un libellé de flux');
      console.log('❌ [ANALYZE] Pas de libellé de flux sélectionné');
      return;
    }

    console.log('✅ [ANALYZE] Démarrage de l\'analyse pour le compte:', selectedAccount, 'libellé:', selectedFeedLabel, 'skipSave:', skipSave);
    setAnalyzing(true);
    setError('');
    setResults(null); // Effacer les résultats précédents
    setShowConfig(false); // Masquer la configuration
    setShowXmlFeeds(false); // Masquer la section XML
    setSelectedCategory(null); // Réinitialiser le filtre de catégorie

    // Sauvegarder automatiquement la configuration avant l'analyse (sauf si skipSave=true pour AUTO-ANALYZE)
    if (!skipSave) {
      await saveConfig(selectedAccount, config, true);
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/tools/product-labeling/analyze`,
        {
          accountId: selectedAccount,
          feedLabel: selectedFeedLabel,
          config: config
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResults(response.data);
      setCurrentPage(1); // Réinitialiser la pagination
      setSelectedCategory(null); // Réinitialiser le filtre de catégorie

      // Stocker aussi les données groupées (retournées par la même requête)
      if (response.data.groups && response.data.groups.length > 0) {
        setGroupAnalysisData({
          success: true,
          groups: response.data.groups,
          summary: response.data.groupSummary,
          stats: response.data.groupStats
        });
        setGroupFilter('');
        setGroupCurrentPage(1);
        setGroupsUnavailableReason(null);
      } else {
        // Stocker la raison d'indisponibilité des groupes
        setGroupsUnavailableReason(response.data.groupsUnavailableReason || 'NO_GROUPS');
        setGroupAnalysisData(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'analyse des produits');
      console.error('Erreur:', err);
    } finally {
      setAnalyzing(false);
    }
  }, [selectedAccount, selectedFeedLabel, token, saveConfig, config]);

  // Lancer automatiquement l'analyse quand un compte et un flux sont sélectionnés (une seule fois)
  // IMPORTANT: Attendre que la config soit chargée (configLoaded) pour éviter d'écraser la config sauvegardée
  useEffect(() => {
    if (selectedAccount && selectedFeedLabel && !analyzing && !hasAutoAnalyzed && configLoaded) {
      console.log('🚀 [AUTO-ANALYZE] Lancement automatique de l\'analyse pour:', { selectedAccount, selectedFeedLabel, configLoaded });
      setHasAutoAnalyzed(true); // Marquer comme analysé pour éviter la boucle
      // skipSave=true car on vient de charger la config, pas besoin de la sauvegarder
      handleAnalyze(true);
    }
  }, [selectedAccount, selectedFeedLabel, analyzing, hasAutoAnalyzed, configLoaded, handleAnalyze]);

  const handleDownloadFeed = async () => {
    /* ⚠️ Consultation seule : la barrière de fond, celle que le masquage ne remplace pas. */
    if (readOnly) return;
    if (!results) return;

    try {
      const response = await axios.post(
        `${API_URL}/api/tools/product-labeling/generate-feed`,
        {
          accountId: selectedAccount,
          results: results
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Créer un lien de téléchargement
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `product-labels-${aujourdhuiIso()}.xml`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Erreur lors de la génération du flux XML');
      console.error('Erreur:', err);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'top': return <TrendingUp className="text-green-500" size={16} />;
      case 'middle': return <Activity className="text-blue-500" size={16} />;
      case 'low': return <TrendingDown className="text-orange-500" size={16} />;
      case 'zero': return <X className="text-red-500" size={16} />;
      case 'zombie': return <Clock className="text-gray-500" size={16} />;
      default: return null;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'top': return 'bg-green-100 text-green-800';
      case 'middle': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-orange-100 text-orange-800';
      case 'zero': return 'bg-red-100 text-red-800';
      case 'zombie': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPieChartColor = (category) => {
    switch (category) {
      case 'TOP': return '#10b981';
      case 'MIDDLE': return '#3b82f6';
      case 'LOW': return '#f59e0b';
      case 'ZERO': return '#ef4444';
      case 'ZOMBIE': return '#6b7280';
      default: return '#6b7280';
    }
  };

  // Préparer les données pour les pie charts
  const preparePieChartData = () => {
    if (!results?.products) return { salesData: [], budgetData: [], productsData: [], conversionValueData: [] };

    const categoryStats = {
      TOP: { conversions: 0, cost: 0, count: 0, conversionsValue: 0 },
      MIDDLE: { conversions: 0, cost: 0, count: 0, conversionsValue: 0 },
      LOW: { conversions: 0, cost: 0, count: 0, conversionsValue: 0 },
      ZERO: { conversions: 0, cost: 0, count: 0, conversionsValue: 0 },
      ZOMBIE: { conversions: 0, cost: 0, count: 0, conversionsValue: 0 }
    };

    results.products.forEach(product => {
      const category = (product.category || '').toUpperCase();
      if (categoryStats[category]) {
        categoryStats[category].conversions += product.conversions || 0;
        categoryStats[category].cost += product.cost || 0;
        categoryStats[category].count += 1;
        categoryStats[category].conversionsValue += product.conversionsValue || 0;
      }
    });

    const salesData = Object.entries(categoryStats)
      .filter(([_, stats]) => stats.conversions > 0)
      .map(([category, stats]) => ({
        name: category,
        value: stats.conversions,
        fill: getPieChartColor(category)
      }));

    const budgetData = Object.entries(categoryStats)
      .filter(([_, stats]) => stats.cost > 0)
      .map(([category, stats]) => ({
        name: category,
        value: stats.cost,
        fill: getPieChartColor(category)
      }));

    const productsData = Object.entries(categoryStats)
      .filter(([_, stats]) => stats.count > 0)
      .map(([category, stats]) => ({
        name: category,
        value: stats.count,
        fill: getPieChartColor(category)
      }));

    const conversionValueData = Object.entries(categoryStats)
      .filter(([_, stats]) => stats.conversionsValue > 0)
      .map(([category, stats]) => ({
        name: category,
        value: stats.conversionsValue,
        fill: getPieChartColor(category)
      }));

    return { salesData, budgetData, productsData, conversionValueData };
  };

  // Fonction de tri
  const handleSort = useCallback((key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Réinitialiser à la première page
  }, [sortConfig]);

  // Fonction pour obtenir la valeur de tri avec logique spécialisée
  const getSortValue = useCallback((product, key) => {
    switch (key) {
      case 'title': return product.title || '';
      case 'category': {
        // Construire la chaîne de catégories depuis les productTypeL1-L5
        const productTypes = [
          product.productTypeL1,
          product.productTypeL2,
          product.productTypeL3,
          product.productTypeL4,
          product.productTypeL5
        ].filter(type => type && type.trim() !== '');
        return productTypes.length > 0 ? productTypes.join(' > ') : '';
      }
      case 'productId': return product.productId || '';
      case 'customLabel0': {
        // Rassembler tous les libellés custom (0 à 4)
        const labels = [
          product.customLabel0,
          product.customLabel1,
          product.customLabel2,
          product.customLabel3,
          product.customLabel4
        ].filter(label => label && label.trim() !== '');
        return labels.join(' | ');
      }
      case 'impressions': return product.impressions || 0;
      case 'ctr': return product.impressions > 0 ? (product.clicks / product.impressions) : 0;
      case 'clicks': return product.clicks || 0;
      case 'cpc': return product.clicks > 0 ? (product.cost / product.clicks) : Number.MAX_VALUE;
      case 'cost': return product.cost || 0;
      case 'conversionRate': return product.clicks > 0 ? (product.conversions / product.clicks) : 0;
      case 'conversions': return product.conversions || 0;
      case 'conversionsValue': return product.conversionsValue || 0;
      case 'cos': return product.conversionsValue > 0 ? (product.cost / product.conversionsValue) * 100 : Number.MAX_VALUE;
      case 'roas': return product.roas || 0;
      case 'performanceLabel': return product.performanceLabel || product.category || '';
      case 'manualTag': return product.manualTag || '';
      default: return 0;
    }
  }, []);

  // Données triées et paginées avec filtrage par catégorie
  const getSortedAndPaginatedProducts = useMemo(() => {
    if (!results?.products) return [];
    
    // Filtrer par catégorie si une catégorie est sélectionnée
    let filteredProducts = results.products;
    if (selectedCategory) {
      filteredProducts = results.products.filter(product => 
        product.category.toUpperCase() === selectedCategory.toUpperCase()
      );
    }
    
    const sorted = [...filteredProducts].sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);
      
      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sorted.slice(startIndex, endIndex);
  }, [results?.products, selectedCategory, sortConfig, currentPage, itemsPerPage]);

  // --- Fonctions pour l'onglet Groupe ---

  // Préparation des données graphiques pour les groupes
  const prepareGroupPieChartData = () => {
    if (!groupAnalysisData?.groups) return { salesData: [], budgetData: [], productsData: [], conversionValueData: [] };

    const categoryStats = {
      TOP: { conversions: 0, cost: 0, count: 0, conversionsValue: 0 },
      MIDDLE: { conversions: 0, cost: 0, count: 0, conversionsValue: 0 },
      LOW: { conversions: 0, cost: 0, count: 0, conversionsValue: 0 },
      ZERO: { conversions: 0, cost: 0, count: 0, conversionsValue: 0 },
      ZOMBIE: { conversions: 0, cost: 0, count: 0, conversionsValue: 0 }
    };

    groupAnalysisData.groups.forEach(group => {
      const category = (group.label || '').toUpperCase();
      if (categoryStats[category]) {
        categoryStats[category].conversions += group.conversions || 0;
        categoryStats[category].cost += group.cost || 0;
        categoryStats[category].count += 1;
        categoryStats[category].conversionsValue += group.conversionsValue || 0;
      }
    });

    const salesData = Object.entries(categoryStats)
      .filter(([_, stats]) => stats.conversions > 0)
      .map(([category, stats]) => ({
        name: category,
        value: stats.conversions,
        fill: getPieChartColor(category)
      }));

    const budgetData = Object.entries(categoryStats)
      .filter(([_, stats]) => stats.cost > 0)
      .map(([category, stats]) => ({
        name: category,
        value: stats.cost,
        fill: getPieChartColor(category)
      }));

    const productsData = Object.entries(categoryStats)
      .filter(([_, stats]) => stats.count > 0)
      .map(([category, stats]) => ({
        name: category,
        value: stats.count,
        fill: getPieChartColor(category)
      }));

    const conversionValueData = Object.entries(categoryStats)
      .filter(([_, stats]) => stats.conversionsValue > 0)
      .map(([category, stats]) => ({
        name: category,
        value: stats.conversionsValue,
        fill: getPieChartColor(category)
      }));

    return { salesData, budgetData, productsData, conversionValueData };
  };

  // Fonction de tri pour les groupes
  const handleGroupSort = useCallback((key) => {
    let direction = 'desc';
    if (groupSortConfig.key === key && groupSortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setGroupSortConfig({ key, direction });
    setGroupCurrentPage(1);
  }, [groupSortConfig]);

  // Fonction pour obtenir la valeur de tri d'un groupe
  const getGroupSortValue = useCallback((group, key) => {
    switch (key) {
      case 'title': return group.title || group.itemGroupId || '';
      case 'productCount': return group.productCount || 0;
      case 'label': return group.label || '';
      case 'impressions': return group.impressions || 0;
      case 'ctr': return group.impressions > 0 ? (group.clicks / group.impressions) : 0;
      case 'clicks': return group.clicks || 0;
      case 'cpc': return group.clicks > 0 ? (group.cost / group.clicks) : Number.MAX_VALUE;
      case 'cost': return group.cost || 0;
      case 'conversionRate': return group.clicks > 0 ? (group.conversions / group.clicks) : 0;
      case 'conversions': return group.conversions || 0;
      case 'conversionsValue': return group.conversionsValue || 0;
      case 'roas': return group.roas || 0;
      default: return 0;
    }
  }, []);

  // Données triées et paginées pour les groupes
  const getSortedAndPaginatedGroups = useMemo(() => {
    if (!groupAnalysisData?.groups) return [];

    let filteredGroups = groupAnalysisData.groups;
    if (groupFilter) {
      filteredGroups = groupAnalysisData.groups.filter(group =>
        group.label?.toUpperCase() === groupFilter.toUpperCase()
      );
    }

    const sorted = [...filteredGroups].sort((a, b) => {
      const aValue = getGroupSortValue(a, groupSortConfig.key);
      const bValue = getGroupSortValue(b, groupSortConfig.key);

      if (typeof aValue === 'string') {
        return groupSortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (aValue < bValue) return groupSortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return groupSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    const startIndex = (groupCurrentPage - 1) * groupItemsPerPage;
    const endIndex = startIndex + groupItemsPerPage;
    return sorted.slice(startIndex, endIndex);
  }, [groupAnalysisData?.groups, groupFilter, groupSortConfig, groupCurrentPage, groupItemsPerPage, getGroupSortValue]);

  // Nombre de groupes filtrés
  const getFilteredGroupsCount = useCallback(() => {
    if (!groupAnalysisData?.groups) return 0;
    if (!groupFilter) return groupAnalysisData.groups.length;
    return groupAnalysisData.groups.filter(g => g.label?.toUpperCase() === groupFilter.toUpperCase()).length;
  }, [groupAnalysisData?.groups, groupFilter]);

  // Total des pages pour les groupes
  const groupTotalPages = Math.ceil(getFilteredGroupsCount() / groupItemsPerPage);

  // Handler pour cliquer sur une catégorie de groupe
  const handleGroupCategoryClick = useCallback((category) => {
    setGroupFilter(groupFilter === category ? '' : category);
    setGroupCurrentPage(1);
  }, [groupFilter]);

  // Filtrage ET tri des produits (optimisé)
  const displayProducts = useMemo(() => {
    if (!products) return [];
    
    // 1. Filtrage par terme de recherche
    let filteredProducts = products;
    if (searchTerm.trim()) {
      filteredProducts = filteredProducts.filter(product => 
        product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.productId.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // 2. Filtrage par catégorie manuelle
    if (selectedManualCategory) {
      filteredProducts = filteredProducts.filter(product => {
        // Construire la hiérarchie complète du produit
        const productTypes = [
          product.productTypeL1,
          product.productTypeL2,
          product.productTypeL3,
          product.productTypeL4,
          product.productTypeL5
        ].filter(type => type && type.trim() !== '');
        
        const productHierarchy = productTypes.join(' > ');
        
        // Vérifier si la hiérarchie du produit commence par la hiérarchie sélectionnée
        return productHierarchy.startsWith(selectedManualCategory);
      });
    }
    
    // 3. Filtrage par libellé manuel
    if (selectedManualLabel) {
      filteredProducts = filteredProducts.filter(product => {
        // Vérifier si le produit contient le libellé sélectionné dans un de ses customLabel0-4
        const productLabels = [
          product.customLabel0,
          product.customLabel1,
          product.customLabel2,
          product.customLabel3,
          product.customLabel4
        ].filter(label => label && label.trim() !== '');
        
        // Vérifier si le libellé sélectionné est présent
        return productLabels.includes(selectedManualLabel);
      });
    }
    
    // 4. Tri des produits filtrés
    const sorted = [...filteredProducts].sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);
      
      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [products, searchTerm, selectedManualCategory, selectedManualLabel, sortConfig]);

  // Produits paginés pour l'affichage
  const paginatedProducts = useMemo(() => {
    const startIndex = (manualCurrentPage - 1) * manualItemsPerPage;
    const endIndex = startIndex + manualItemsPerPage;
    return displayProducts.slice(startIndex, endIndex);
  }, [displayProducts, manualCurrentPage, manualItemsPerPage]);

  // Calcul du nombre total de pages
  const manualTotalPages = useMemo(() => {
    return Math.ceil(displayProducts.length / manualItemsPerPage);
  }, [displayProducts.length, manualItemsPerPage]);

  // Reset page quand les filtres changent
  useEffect(() => {
    setManualCurrentPage(1);
  }, [searchTerm, selectedManualCategory, selectedManualLabel, selectedAccount, selectedFeedLabel]);

  // Fonction pour obtenir la couleur d'un tag basé sur son hash
  const getTagColor = useCallback((tag) => {
    if (!tag) return { background: '#6b7280', color: '#ffffff' };
    
    // Générer un hash simple pour le tag
    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
      const char = tag.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convertir en entier 32bits
    }
    
    // Palette de couleurs unies comme les labels automatiques
    const colors = [
      { background: '#dbeafe', color: '#1e40af' }, // Bleu (comme label-middle)
      { background: '#dcfce7', color: '#166534' }, // Vert (comme label-top)
      { background: '#fed7aa', color: '#9a3412' }, // Orange (comme label-low)
      { background: '#fce7f3', color: '#be185d' }, // Rose
      { background: '#fee2e2', color: '#991b1b' }, // Rouge (comme label-zero)
      { background: '#e0f2fe', color: '#0c4a6e' }, // Cyan
      { background: '#f0fdf4', color: '#14532d' }, // Vert clair
      { background: '#fef3c7', color: '#92400e' }, // Jaune
    ];
    
    return colors[Math.abs(hash) % colors.length];
  }, []);



  // Gestionnaires d'événements super-simplifiés (aucune logique supplémentaire)
  const handleEditTagChange = useCallback((e) => {
    setEditingTag(e.target.value);
  }, []);

  const handleBulkTagChange = useCallback((e) => {
    setBulkTag(e.target.value);
  }, []);

  // Récupérer les tags existants au chargement des produits pour initialiser les suggestions (optimisé)
  // Les tags sont maintenant spécifiques au flux sélectionné
  useEffect(() => {
    if (products && products.length > 0 && selectedFeedLabel) {
      const existingTags = new Set();
      for (const product of products) {
        if (product.manualTag?.trim()) {
          existingTags.add(product.manualTag.trim());
        }
      }
      // Seulement mettre à jour si les tags ont changé
      setTagSuggestions(prev => {
        if (prev.size === existingTags.size && [...prev].every(tag => existingTags.has(tag))) {
          return prev; // Pas de changement, éviter la re-render
        }
        return existingTags;
      });
    } else if (!selectedFeedLabel) {
      // Réinitialiser les suggestions si pas de flux sélectionné
      setTagSuggestions(new Set());
    }
  }, [products, selectedFeedLabel]);

  // Gestionnaire de clic sur les cartes de catégories
  const handleCategoryClick = (category) => {
    if (selectedCategory === category) {
      // Si la même catégorie est cliquée, désactiver le filtre
      setSelectedCategory(null);
    } else {
      // Sinon, activer le filtre pour cette catégorie
      setSelectedCategory(category);
    }
    // Réinitialiser à la première page
    setCurrentPage(1);
  };

  // Calcul du nombre total de pages avec filtrage
  const getFilteredProductsCount = () => {
    if (!results?.products) return 0;
    if (!selectedCategory) return results.products.length;
    return results.products.filter(product => 
      product.category.toUpperCase() === selectedCategory.toUpperCase()
    ).length;
  };
  
  const totalPages = Math.ceil(getFilteredProductsCount() / itemsPerPage);

  return (
    <div className="product-labeling-tool">
      {/* En-tête dans un conteneur */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827',
              display: 'flex',
              alignItems: 'center',
              margin: '0 0 8px 0'
            }}>
              <Tag style={{ marginRight: '8px', height: '24px', width: '24px', color: '#f59e0b' }} />
              Étiquetage de Produits Shopping
            </h2>
            <p style={{ color: '#6b7280', margin: '0', fontSize: '16px' }}>
              Analysez les performances de vos produits et générez automatiquement des custom_label
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {selectedFeedLabel && (activeTab === 'automatic' || activeTab === 'group') && (
              <button
                onClick={() => {
                  setShowConfig(!showConfig);
                  setShowXmlFeeds(false); // Fermer le flux XML
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  border: hasCustomSettings && !showConfig ? '1px solid #16a34a' : '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: showConfig ? '#f3f4f6' : hasCustomSettings ? '#f0fdf4' : 'white',
                  color: hasCustomSettings && !showConfig ? '#166534' : '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                title={showConfig ? "Masquer la configuration" : "Afficher la configuration"}
              >
                <Settings size={16} style={{ marginRight: '8px' }} />
                {hasCustomSettings ? 'Paramètres personnalisés' : 'Paramètres'}
              </button>
            )}

            {selectedAccount && selectedFeedLabel && (
              <button
                onClick={() => {
                  setShowXmlFeeds(!showXmlFeeds);
                  setShowConfig(false); // Fermer la configuration
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: showXmlFeeds ? '#f3f4f6' : 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                <Download size={16} style={{ marginRight: '8px' }} />
                Récupérer le flux XML
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sélecteur de compte - même style que les autres outils */}
      {/* Sans compte, la carte du sélecteur n'a rien à encadrer : on affiche
          l'état vide seul, plutôt que de le loger dans un cadre dans un cadre. */}
      {!loading && !filteringAccounts && accounts.length === 0 ? (
        <AucunCompteGoogleAds token={token} />
      ) : (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#111827', margin: '0 0 12px 0' }}>
            Compte Google Ads
          </h3>

          {(loading || filteringAccounts) ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{
                display: 'inline-block',
                width: '20px',
                height: '20px',
                border: '2px solid #f3f3f3',
                borderTop: '2px solid #dc2626',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ margin: '8px 0 0 0', color: '#6b7280' }}>Chargement des comptes...</p>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', minWidth: '100px' }}>
                Sélectionner :
              </label>
              <select
                value={selectedAccount || pendingAccountId || ''}
                onChange={async (e) => {
                  const accountId = e.target.value;
                  if (!accountId) {
                    setSelectedAccount('');
                    setPendingAccountId(null);
                    setMerchantCenterMissing(false);
                    return;
                  }

                  // Stocker le compte en attente
                  setPendingAccountId(accountId);
                  setMerchantCenterMissing(false);

                  // Vérifier si un Merchant Center est associé
                  const hasMerchant = await checkMerchantCenterAssociation(accountId);

                  if (hasMerchant) {
                    // Merchant Center trouvé, on peut continuer
                    setSelectedAccount(accountId);
                    setPendingAccountId(null);
                    if (updateUrlWithAccount) updateUrlWithAccount(accountId);
                  } else {
                    // Pas de Merchant Center, on garde le compte en attente
                    setSelectedAccount('');
                  }
                }}
                disabled={loading || filteringAccounts || checkingMerchantCenter}
                style={{
                  flex: '1',
                  minWidth: '300px',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white'
                }}
              >
                <option value="">-- Choisir un compte --</option>
                {accounts.map(account => (
                  <option key={account.id} value={account.sourceId || account.id}>
                    {account.name}
                  </option>
                ))}
              </select>

              {selectedAccount && (
                <>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginLeft: '20px' }}>
                    Flux :
                  </label>
                  <select
                    value={selectedFeedLabel}
                    onChange={(e) => {
                      setSelectedFeedLabel(e.target.value);
                      // Effacer les résultats précédents quand on change de libellé
                      setResults(null);
                      setError('');
                      setCurrentPage(1);
                      setSelectedCategory(null);
                    }}
                    disabled={loadingFeedLabels}
                    style={{
                      minWidth: '200px',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      background: 'white'
                    }}
                  >
                    <option value="">
                      {loadingFeedLabels ? 'Chargement...' : '-- Choisir un flux --'}
                    </option>
                    {feedLabels.map(label => (
                      <option key={label} value={label}>
                        {label}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
          )}

          {/* Message si Merchant Center manquant */}
          {merchantCenterMissing && pendingAccountId && !checkingMerchantCenter && (
            <div style={{
              marginTop: '16px',
              padding: '20px',
              backgroundColor: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <Store size={24} style={{ color: '#d97706', flexShrink: 0, marginTop: '2px' }} />
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#92400e', fontWeight: '600' }}>
                    Compte Merchant Center requis
                  </h4>
                  {/*
                    ⚠️ **À un client, on dit à qui s'adresser — et rien d'autre.**

                    Il ne connecte rien et n'ajoute aucun compte : il reçoit ce que son agence
                    lui ouvre. Les deux gestes proposés ici mènent à l'écran Connecteurs et à
                    l'ajout d'un compte, où il n'a pas sa place — et c'est de l'outil qu'il
                    aurait conclu qu'il est cassé. Même règle que l'Optimiseur de flux, qui ne
                    lui propose pas d'analyser un flux, et que l'accueil, qui ne lui propose
                    pas de connecter une source.

                    ⚠️ Et le second message était **faux pour lui** : « aucun connecteur
                    configuré » se lit sur `globalConnectors`, que son rôle laisse vide. Il
                    accusait un réglage d'agence parfaitement en place.
                  */}
                  {readOnly ? (
                    <p style={{ margin: 0, color: '#78350f', fontSize: '14px', lineHeight: '1.5' }}>
                      Votre agence n'a pas encore associé de compte Merchant Center à ce compte
                      Google Ads. Les produits ne peuvent pas être étiquetés tant que ce n'est
                      pas fait.
                    </p>
                  ) : (
                  <>
                  <p style={{ margin: '0 0 16px 0', color: '#78350f', fontSize: '14px', lineHeight: '1.5' }}>
                    Pour utiliser l'outil d'étiquetage de produits, vous devez associer un compte Google Merchant Center au compte Google Ads sélectionné.
                    {suggestedMerchantIds.length > 0 && (
                      <span> Nous avons détecté {suggestedMerchantIds.length} compte(s) Merchant Center lié(s) à ce compte Google Ads.</span>
                    )}
                  </p>

                  {/* Vérifier si un connecteur Merchant API existe */}
                  {globalConnectors.some(c => c.provider === 'GOOGLE_MERCHANT_API') ? (
                    <button
                      onClick={handleConnectMerchantCenter}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#d97706'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#f59e0b'}
                    >
                      <Store size={18} />
                      Ajouter un compte Merchant Center
                    </button>
                  ) : (
                    <div>
                      <p style={{ margin: '0 0 12px 0', color: '#78350f', fontSize: '14px' }}>
                        Aucun connecteur Google Merchant Center n'est configuré. Connectez-vous d'abord à Google Merchant Center.
                      </p>
                      <button
                        onClick={() => setCurrentView && setCurrentView('connectors')}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#1a1a1a',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <Store size={18} />
                        Configurer les connecteurs
                      </button>
                    </div>
                  )}
                  </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Système d'onglets */}
      {selectedAccount && selectedFeedLabel && (
        <div className="tabs-container">
          <div className="tabs-header">
            <button
              className={`tab-button ${activeTab === 'automatic' ? 'active' : ''}`}
              onClick={() => { setActiveTab('automatic'); setShowXmlFeeds(false); }}
            >
              <Settings size={16} />
              Étiquetage automatique (produit)
            </button>
            <button
              className={`tab-button ${activeTab === 'group' ? 'active' : ''}`}
              onClick={() => { setActiveTab('group'); setShowXmlFeeds(false); }}
            >
              <Layers size={16} />
              Étiquetage automatique (groupe de produits)
            </button>
            <button
              className={`tab-button ${activeTab === 'manual' ? 'active' : ''}`}
              onClick={() => { setActiveTab('manual'); setShowXmlFeeds(false); }}
            >
              <Tags size={16} />
              Étiquetage manuel
            </button>
          </div>
        </div>
      )}

      {/* Configuration partagée entre Étiquetage automatique (produit) et Étiquetage automatique (groupe de produits) */}
      {(activeTab === 'automatic' || activeTab === 'group') && selectedAccount && selectedFeedLabel && showConfig && (
        <div className="config-section">
          <div className="config-header">
            <Settings size={20} />
            <h3>Paramètres</h3>
          </div>

          <div className="config-grid">

            <div className="config-group">
              <label>Période d'analyse (jours)</label>
              <input
                type="number"
                value={config.analysePeriodDays}
                onChange={(e) => setConfig({...config, analysePeriodDays: parseInt(e.target.value)})}
                min="1"
                max="90"
              />
            </div>

            <div className="config-group">
              <label>Seuil ventes TOP</label>
              <input
                type="number"
                value={config.topSalesThreshold}
                onChange={(e) => setConfig({...config, topSalesThreshold: parseInt(e.target.value)})}
                min="1"
              />
            </div>

            <div className="config-group">
              <label>Seuil ROAS TOP (%)</label>
              <input
                type="number"
                value={config.topRoasThreshold}
                onChange={(e) => setConfig({...config, topRoasThreshold: parseInt(e.target.value)})}
                min="100"
              />
            </div>

            <div className="config-group">
              <label>Seuil ROAS MIDDLE (%)</label>
              <input
                type="number"
                value={config.middleRoasThreshold}
                onChange={(e) => setConfig({...config, middleRoasThreshold: parseInt(e.target.value)})}
                min="100"
              />
            </div>

          </div>

          {/* Message d'aide et bouton de sauvegarde centré en bas */}
          <div style={{ textAlign: 'center', marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #e5e7eb' }}>
            {(!selectedAccount || !selectedFeedLabel) && (
              <div style={{
                marginBottom: '10px',
                padding: '8px',
                backgroundColor: '#fef3c7',
                color: '#92400e',
                borderRadius: '4px',
                fontSize: '14px'
              }}>
                {!selectedAccount && 'Veuillez sélectionner un compte. '}
                {!selectedFeedLabel && 'Veuillez sélectionner un libellé de flux. '}
              </div>
            )}
            {/* Consultation seule : les seuils sont un réglage d'agence. */}
            {!readOnly && (
            <button
              onClick={async () => {
                const success = await saveConfig(selectedAccount, config);
                if (success) {
                  setShowConfig(false); // Fermer la fenêtre après sauvegarde réussie
                  // Relancer l'analyse (retourne les produits ET les groupes)
                  await handleAnalyze();
                }
              }}
              disabled={!selectedAccount || !selectedFeedLabel}
              style={{
                padding: '8px 16px',
                backgroundColor: '#1a1a1a',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: (selectedAccount && selectedFeedLabel) ? 'pointer' : 'not-allowed',
                opacity: (selectedAccount && selectedFeedLabel) ? 1 : 0.5
              }}
            >
              Sauvegarder
            </button>
            )}
          </div>
        </div>
      )}

      {/* Onglet Étiquetage automatique (produit) */}
      {activeTab === 'automatic' && selectedAccount && selectedFeedLabel && (
        <>
      {/* Erreur */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Écran de chargement pendant l'analyse */}
      {analyzing && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '3rem',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          border: '1px solid #e2e8f0',
          margin: '2rem 0',
          minHeight: '400px'
        }}>
          {/* Spinner animé */}
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #1a1a1a',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '1.5rem'
          }}></div>
          
          {/* Texte de chargement */}
          <h3 style={{
            color: '#374151',
            fontSize: '1.25rem',
            fontWeight: '600',
            marginBottom: '0.5rem',
            textAlign: 'center'
          }}>
            🔍 Analyse en cours...
          </h3>
          
          <p style={{
            color: '#64748b',
            fontSize: '0.875rem',
            textAlign: 'center',
            maxWidth: '400px',
            lineHeight: '1.5'
          }}>
            Récupération et analyse des données de performance de vos produits Shopping. 
            Cela peut prendre quelques instants selon la taille de votre catalogue.
          </p>

          {/* Style CSS pour l'animation du spinner */}
          <style dangerouslySetInnerHTML={{
            __html: `
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `
          }} />
        </div>
      )}

      {/* Résultats */}
      {results && !showXmlFeeds && (
        <div className="results-section">
          {/* Graphiques de distribution */}
          {(() => {
            const { salesData, budgetData, productsData, conversionValueData } = preparePieChartData();
            return (salesData.length > 0 || budgetData.length > 0 || productsData.length > 0 || conversionValueData.length > 0) && (
              <div className="charts-section">
                <h3>Distribution par catégorie (Produits)</h3>
                <div className="charts-container">
                  {productsData.length > 0 && (
                    <div className="chart-wrapper">
                      <h4>Répartition des produits</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={productsData}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            dataKey="value"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                          >
                            {productsData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => [value, 'Produits']} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {salesData.length > 0 && (
                    <div className="chart-wrapper">
                      <h4>Répartition des ventes</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={salesData}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            dataKey="value"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                          >
                            {salesData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => [value.toFixed(1), 'Conversions']} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {budgetData.length > 0 && (
                    <div className="chart-wrapper">
                      <h4>Répartition du budget</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={budgetData}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            dataKey="value"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                          >
                            {budgetData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => [`€${value.toFixed(2)}`, 'Coût']} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {conversionValueData.length > 0 && (
                    <div className="chart-wrapper">
                      <h4>Répartition valeur de conversion</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={conversionValueData}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            dataKey="value"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                          >
                            {conversionValueData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => [`€${value.toFixed(2)}`, 'Valeur']} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          <h3>Résultats de l'analyse</h3>
          
          {/* Statistiques globales */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{results.summary?.total || 0}</div>
              <div className="stat-label">Produits analysés</div>
            </div>
            <div 
              className={`stat-card top clickable ${selectedCategory === 'TOP' ? 'active' : ''}`}
              onClick={() => handleCategoryClick('TOP')}
              title="Cliquer pour filtrer les produits TOP"
            >
              <div className="stat-number">{results.summary?.top || 0}</div>
              <div className="stat-label">TOP performers</div>
            </div>
            <div 
              className={`stat-card middle clickable ${selectedCategory === 'MIDDLE' ? 'active' : ''}`}
              onClick={() => handleCategoryClick('MIDDLE')}
              title="Cliquer pour filtrer les produits MIDDLE"
            >
              <div className="stat-number">{results.summary?.middle || 0}</div>
              <div className="stat-label">MIDDLE performers</div>
            </div>
            <div 
              className={`stat-card low clickable ${selectedCategory === 'LOW' ? 'active' : ''}`}
              onClick={() => handleCategoryClick('LOW')}
              title="Cliquer pour filtrer les produits LOW"
            >
              <div className="stat-number">{results.summary?.low || 0}</div>
              <div className="stat-label">LOW performers</div>
            </div>
            <div 
              className={`stat-card zero clickable ${selectedCategory === 'ZERO' ? 'active' : ''}`}
              onClick={() => handleCategoryClick('ZERO')}
              title="Cliquer pour filtrer les produits ZÉRO vente"
            >
              <div className="stat-number">{results.summary?.zero || 0}</div>
              <div className="stat-label">ZÉRO vente</div>
            </div>
            <div 
              className={`stat-card zombie clickable ${selectedCategory === 'ZOMBIE' ? 'active' : ''}`}
              onClick={() => handleCategoryClick('ZOMBIE')}
              title="Cliquer pour filtrer les produits ZOMBIES"
            >
              <div className="stat-number">{results.summary?.zombie || 0}</div>
              <div className="stat-label">ZOMBIES</div>
            </div>
          </div>

          {/* Tableau des produits */}
          {results.products && results.products.length > 0 && (
            <div className="products-preview">
              {selectedCategory && (
                <div className="filter-indicator">
                  Filtre actif: {selectedCategory} ({getFilteredProductsCount()} sur {results.products.length})
                  <button 
                    onClick={() => setSelectedCategory(null)}
                    className="clear-filter-btn"
                    title="Supprimer le filtre"
                  >
                    ✕
                  </button>
                </div>
              )}
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '60px' }}>Image</th>
                      <th onClick={() => handleSort('title')} className="sortable">
                        Produit {sortConfig.key === 'title' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('category')} className="sortable">
                        Catégorie {sortConfig.key === 'category' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('impressions')} className="sortable">
                        Impressions {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('ctr')} className="sortable">
                        CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('clicks')} className="sortable">
                        Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('cpc')} className="sortable">
                        CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('cost')} className="sortable">
                        Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('conversionRate')} className="sortable">
                        Taux conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('conversions')} className="sortable">
                        Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('conversionsValue')} className="sortable">
                        Valeur conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('roas')} className="sortable">
                        ROAS {sortConfig.key === 'roas' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedAndPaginatedProducts.map((product, index) => (
                      <tr key={index}>
                        <td style={{ padding: '4px', textAlign: 'center' }}>
                          {product.imageUrl ? (
                            <img
                              src={adresseDeVignette(product)}
                              alt={product.title}
                              loading="lazy"
                              decoding="async"
                              fetchPriority="low"
                              width="50"
                              height="50"
                              style={{
                                width: '50px',
                                height: '50px',
                                objectFit: 'contain',
                                borderRadius: '4px',
                                backgroundColor: '#f9fafb'
                              }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div style={{
                              width: '50px',
                              height: '50px',
                              backgroundColor: '#f3f4f6',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#9ca3af',
                              fontSize: '10px'
                            }}>
                              N/A
                            </div>
                          )}
                        </td>
                        <td className="product-info">
                          <div className="product-title">{product.title}</div>
                          <div className="product-id">{product.id}</div>
                        </td>
                        <td>
                          <span className={`category-badge ${getCategoryColor(product.category)}`}>
                            {getCategoryIcon(product.category)}
                            {product.category.toUpperCase()}
                          </span>
                        </td>
                        <td>{product.impressions || 0}</td>
                        <td>{product.impressions > 0 ? ((product.clicks / product.impressions) * 100).toFixed(2) + '%' : '0%'}</td>
                        <td>{product.clicks || 0}</td>
                        <td>€{product.clicks > 0 ? (product.cost / product.clicks).toFixed(2) : '0.00'}</td>
                        <td>€{(product.cost || 0).toFixed(2)}</td>
                        <td>{product.clicks > 0 ? ((product.conversions / product.clicks) * 100).toFixed(2) + '%' : '0%'}</td>
                        <td>{product.conversions || 0}</td>
                        <td>€{(product.conversionsValue || 0).toFixed(2)}</td>
                        <td>{product.roas ? `${product.roas}%` : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Contrôles de pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <span>
                      Affichage {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, getFilteredProductsCount())} sur {getFilteredProductsCount()} produits
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    <button 
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      ← Précédent
                    </button>
                    <span className="page-numbers">
                      Page {currentPage} sur {totalPages}
                    </span>
                    <button 
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
        </>
      )}

      {/* Section Gestion des flux XML - SORTIE DES ONGLETS pour être visible dans les deux */}
      {showXmlFeeds && !showConfig && selectedAccount && selectedFeedLabel && (
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          backgroundColor: '#f8fafc',
          borderRadius: '0.5rem',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#1e293b',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              🔄 Gestion du flux XML
            </h3>
            <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
              Configurez les libellés pour l'étiquetage automatique et manuel, puis programmez la mise à jour automatique du flux XML.
            </p>
            
            {/* Onglets XML */}
            <div style={{
              display: 'flex',
              borderBottom: '1px solid #e2e8f0',
              marginTop: '1rem',
              marginBottom: '0'
            }}>
              {[
                { key: 'configuration', label: '🏷️ Configuration', icon: '🏷️' },
                { key: 'programmation', label: '⏰ Programmation', icon: '⏰' },
                { key: 'flux', label: '🔗 Flux', icon: '🔗' },
                { key: 'journal', label: '📋 Journal', icon: '📋' }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveXmlTab(tab.key)}
                  style={{
                    padding: '0.75rem 1rem',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    borderBottom: activeXmlTab === tab.key ? '2px solid #1a1a1a' : '2px solid transparent',
                    color: activeXmlTab === tab.key ? '#1a1a1a' : '#6b7280',
                    fontWeight: activeXmlTab === tab.key ? '600' : '400',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (activeXmlTab !== tab.key) {
                      e.target.style.color = '#1a1a1a';
                      e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeXmlTab !== tab.key) {
                      e.target.style.color = '#6b7280';
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Configuration des libellés */}
          {activeXmlTab === 'configuration' && (
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '0.5rem',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <h4 style={{ 
              fontWeight: '600', 
              color: '#374151',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              🏷️ Configuration des libellés
            </h4>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1rem'
            }}>
              {/* Libellé pour l'étiquetage automatique (produit) */}
              <div style={{
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                backgroundColor: '#f9fafb'
              }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  Libellé pour l'étiquetage automatique (produit)
                </label>
                <select
                  value={config.customLabel}
                  onChange={(e) => setConfig({...config, customLabel: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="">
                    {loadingLabelStatuses ? 'Chargement des statuts...' : 'Choisir un libellé pour l\'étiquetage automatique (produit)'}
                  </option>
                  <option value="custom_label_0">{getLabelStatusEmoji('custom_label_0')}custom_label_0{getLabelStatusText('custom_label_0')}</option>
                  <option value="custom_label_1">{getLabelStatusEmoji('custom_label_1')}custom_label_1{getLabelStatusText('custom_label_1')}</option>
                  <option value="custom_label_2">{getLabelStatusEmoji('custom_label_2')}custom_label_2{getLabelStatusText('custom_label_2')}</option>
                  <option value="custom_label_3">{getLabelStatusEmoji('custom_label_3')}custom_label_3{getLabelStatusText('custom_label_3')}</option>
                  <option value="custom_label_4">{getLabelStatusEmoji('custom_label_4')}custom_label_4{getLabelStatusText('custom_label_4')}</option>
                </select>
                <p style={{
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  marginTop: '0.5rem',
                  margin: '0.5rem 0 0 0'
                }}>
                  Libellé utilisé par l'analyse automatique des produits
                </p>
              </div>

              {/* Libellé pour l'étiquetage automatique (groupe de produits) */}
              <div style={{
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                backgroundColor: '#f9fafb'
              }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  Libellé pour l'étiquetage automatique (groupe de produits)
                </label>
                <select
                  value={config.groupCustomLabel || ''}
                  onChange={(e) => setConfig({...config, groupCustomLabel: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="">
                    {loadingLabelStatuses ? 'Chargement des statuts...' : 'Choisir un libellé pour l\'étiquetage automatique (groupe de produits)'}
                  </option>
                  <option value="custom_label_0">{getLabelStatusEmoji('custom_label_0')}custom_label_0{getLabelStatusText('custom_label_0')}</option>
                  <option value="custom_label_1">{getLabelStatusEmoji('custom_label_1')}custom_label_1{getLabelStatusText('custom_label_1')}</option>
                  <option value="custom_label_2">{getLabelStatusEmoji('custom_label_2')}custom_label_2{getLabelStatusText('custom_label_2')}</option>
                  <option value="custom_label_3">{getLabelStatusEmoji('custom_label_3')}custom_label_3{getLabelStatusText('custom_label_3')}</option>
                  <option value="custom_label_4">{getLabelStatusEmoji('custom_label_4')}custom_label_4{getLabelStatusText('custom_label_4')}</option>
                </select>
                <p style={{
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  marginTop: '0.5rem',
                  margin: '0.5rem 0 0 0'
                }}>
                  Libellé basé sur les performances agrégées par item_group_id
                </p>
              </div>

              {/* Libellé pour l'étiquetage manuel */}
              <div style={{
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                backgroundColor: '#f9fafb'
              }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  Libellé pour l'étiquetage manuel
                </label>
                <select
                  value={config.manualCustomLabel}
                  onChange={(e) => setConfig({...config, manualCustomLabel: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="">
                    {loadingLabelStatuses ? 'Chargement des statuts...' : 'Choisir un libellé pour l\'étiquetage manuel'}
                  </option>
                  <option value="custom_label_0">{getLabelStatusEmoji('custom_label_0')}custom_label_0{getLabelStatusText('custom_label_0')}</option>
                  <option value="custom_label_1">{getLabelStatusEmoji('custom_label_1')}custom_label_1{getLabelStatusText('custom_label_1')}</option>
                  <option value="custom_label_2">{getLabelStatusEmoji('custom_label_2')}custom_label_2{getLabelStatusText('custom_label_2')}</option>
                  <option value="custom_label_3">{getLabelStatusEmoji('custom_label_3')}custom_label_3{getLabelStatusText('custom_label_3')}</option>
                  <option value="custom_label_4">{getLabelStatusEmoji('custom_label_4')}custom_label_4{getLabelStatusText('custom_label_4')}</option>
                </select>
                <p style={{
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  marginTop: '0.5rem',
                  margin: '0.5rem 0 0 0'
                }}>
                  Libellé utilisé par l'étiquetage manuel des produits
                </p>
              </div>
            </div>
            
            {/* Bouton de sauvegarde des libellés */}
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              {/* Consultation seule : les libellés sont un réglage d'agence. */}
              {!readOnly && (
              <button
                onClick={async () => {
                  const success = await saveConfig(selectedAccount, config);
                  if (success) {
                    console.log('✅ Configuration des libellés sauvegardée');
                    alert('Configuration des libellés sauvegardée avec succès !');
                  }
                }}
                disabled={!selectedAccount || !selectedFeedLabel}
                style={{ 
                  padding: '0.5rem 1rem', 
                  backgroundColor: '#1a1a1a', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '0.375rem',
                  cursor: (selectedAccount && selectedFeedLabel) ? 'pointer' : 'not-allowed',
                  opacity: (selectedAccount && selectedFeedLabel) ? 1 : 0.5,
                  fontSize: '0.875rem',
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)'
                }}
                onMouseEnter={(e) => {
                  if (selectedAccount && selectedFeedLabel) {
                    e.target.style.backgroundColor = '#333333';
                    e.target.style.transform = 'scale(1.05)';
                    e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedAccount && selectedFeedLabel) {
                    e.target.style.backgroundColor = '#1a1a1a';
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                Sauvegarder les libellés
              </button>
              )}
            </div>
          </div>
          )}
          
          {/* Sélection de l'heure de mise à jour */}
          {activeXmlTab === 'programmation' && (
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '0.5rem',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <h4 style={{ 
              fontWeight: '600', 
              color: '#374151',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Clock size={16} />
              Heure de mise à jour automatique
            </h4>
            <p style={{ 
              fontSize: '0.875rem', 
              color: '#64748b',
              marginBottom: '0.75rem'
            }}>
              Fuseau horaire du compte : {timezone}
            </p>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.75rem' 
            }}>
              <label style={{ 
                fontSize: '0.875rem', 
                color: '#64748b' 
              }}>
                Mise à jour quotidienne à :
              </label>
              <input
                type="time"
                value={updateTime}
                onChange={(e) => setUpdateTime(e.target.value)}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
              />
              {/* Consultation seule : l'heure de mise à jour est un réglage d'agence. */}
              {!readOnly && (
              <button
                onClick={saveUpdateTime}
                style={{
                  padding: '0.5rem 0.75rem',
                  backgroundColor: '#1a1a1a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#333333';
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#1a1a1a';
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                Sauvegarder
              </button>
              )}
              <span style={{ 
                fontSize: '0.875rem', 
                color: '#64748b',
                marginLeft: '0.5rem'
              }}>
                Dernière mise à jour automatique : {updateLogs.length > 0 ? new Date(updateLogs[0]?.createdAt).toLocaleString('fr-FR') : 'Jamais'}
              </span>
            </div>
          </div>
          )}
          
          {/* Section Flux XML */}
          {activeXmlTab === 'flux' && (
          <>
          {error && (
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              marginBottom: '1rem'
            }}>
              {error}
            </div>
          )}
          
          {/* URL du flux XML unique */}
          {(config.customLabel || config.manualCustomLabel) && (
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '0.5rem',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <h4 style={{ 
                fontWeight: '600', 
                color: '#374151',
                marginBottom: '0.5rem' 
              }}>
                URL de votre flux XML unique
              </h4>
              <p style={{ 
                color: '#64748b', 
                fontSize: '0.875rem',
                marginBottom: '0.75rem' 
              }}>
                Ce flux XML unique combine les données de l'étiquetage automatique et manuel :
              </p>
              
              {/* Informations sur les libellés configurés */}
              <div style={{
                backgroundColor: '#f9fafb',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                padding: '0.75rem',
                marginBottom: '1rem',
                fontSize: '0.875rem'
              }}>
                <div style={{ color: '#374151', fontWeight: '500', marginBottom: '0.5rem' }}>
                  📋 Contenu du flux XML :
                </div>
                {config.customLabel && (
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>
                    • Étiquetage automatique (produit) → {config.customLabel}
                  </div>
                )}
                {config.groupCustomLabel && (
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>
                    • Étiquetage automatique (groupe de produits) → {config.groupCustomLabel}
                  </div>
                )}
                {config.manualCustomLabel && (
                  <div style={{ color: '#64748b' }}>
                    • Étiquetage manuel → {config.manualCustomLabel}
                  </div>
                )}
              </div>
              
              {/* URL unique du flux */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem' 
              }}>
                <input
                  type="text"
                  value={`${API_URL}/api/tools/product-labeling/account-feed/${encodeURIComponent(selectedFeedLabel)}/${selectedAccount}`}
                  readOnly
                  style={{
                    flex: '1',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.875rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    backgroundColor: 'white'
                  }}
                />
                <button
                  onClick={() => navigator.clipboard.writeText(`${API_URL}/api/tools/product-labeling/account-feed/${encodeURIComponent(selectedFeedLabel)}/${selectedAccount}`)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    backgroundColor: '#1a1a1a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#333333';
                    e.target.style.transform = 'scale(1.05)';
                    e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#1a1a1a';
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  Copier
                </button>
                <button
                  onClick={() => window.open(`${API_URL}/api/tools/product-labeling/account-feed/${encodeURIComponent(selectedFeedLabel)}/${selectedAccount}`, '_blank')}
                  style={{
                    padding: '0.5rem',
                    color: '#1a1a1a',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    transition: 'all 0.2s ease',
                    borderRadius: '0.375rem'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
                    e.target.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.transform = 'scale(1)';
                  }}
                  title="Ouvrir le flux XML"
                >
                  🔗
                </button>
              </div>
              
              <p style={{
                fontSize: '0.75rem',
                color: '#64748b',
                marginTop: '0.75rem',
                margin: '0.75rem 0 0 0',
                fontStyle: 'italic'
              }}>
                💡 Utilisez cette URL unique dans Google Merchant Center. Elle contient automatiquement tous les libellés configurés.
              </p>
            </div>
          )}
          
          {!config.customLabel && !config.manualCustomLabel && (
            <div style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '0.5rem',
              padding: '1rem',
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              <p style={{ 
                color: '#92400e', 
                fontSize: '0.875rem',
                margin: '0'
              }}>
                ⚠️ Veuillez configurer au moins un libellé (automatique ou manuel) pour générer l'URL du flux XML.
              </p>
            </div>
          )}
          </>
          )}
          
          {/* Journal des mises à jour */}
          {activeXmlTab === 'journal' && (
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '0.5rem',
            padding: '1rem'
          }}>
            <h4 style={{ 
              fontWeight: '600', 
              color: '#374151',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              📋 Journal des mises à jour (10 dernières)
            </h4>
            
            {updateLogs.length > 0 ? (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                          Date et heure
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                          Fuseau horaire
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                          Total produits
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                          TOP
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                          MIDDLE
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                          LOW
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                          ZERO
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                          ZOMBIES
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                          Statut
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {updateLogs.map((log, index) => {
                        const stats = log.productsStats ? (typeof log.productsStats === 'string' ? JSON.parse(log.productsStats) : log.productsStats) : {};
                        return (
                          <tr key={log.id || index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                              {new Date(log.createdAt).toLocaleString('fr-FR')}
                            </td>
                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#64748b' }}>
                              {timezone}
                            </td>
                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600' }}>
                              {log.productsCount || 0}
                            </td>
                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#059669' }}>
                              {stats.TOP || 0}
                            </td>
                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#1a1a1a' }}>
                              {stats.MIDDLE || 0}
                            </td>
                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#f59e0b' }}>
                              {stats.LOW || 0}
                            </td>
                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#ef4444' }}>
                              {stats.ZERO || 0}
                            </td>
                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
                              {stats.ZOMBIE || 0}
                            </td>
                            <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                              <span style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                backgroundColor: log.status === 'COMPLETED' ? '#dcfce7' : log.status === 'FAILED' ? '#fef2f2' : '#fef3c7',
                                color: log.status === 'COMPLETED' ? '#166534' : log.status === 'FAILED' ? '#dc2626' : '#92400e'
                              }}>
                                {log.status === 'COMPLETED' ? 'Succès' : log.status === 'FAILED' ? 'Échec' : 'En cours'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination du journal */}
                {totalLogs > logsPerPage && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '1rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid #e2e8f0'
                  }}>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      Affichage de {((logsCurrentPage - 1) * logsPerPage) + 1} à {Math.min(logsCurrentPage * logsPerPage, totalLogs)} sur {totalLogs} mises à jour
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => selectedFeedLabel && loadUpdateLogs(selectedAccount, selectedFeedLabel, logsCurrentPage - 1)}
                        disabled={logsCurrentPage === 1 || !selectedFeedLabel}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.375rem',
                          backgroundColor: logsCurrentPage === 1 ? '#f9fafb' : 'white',
                          color: logsCurrentPage === 1 ? '#9ca3af' : '#374151',
                          cursor: logsCurrentPage === 1 ? 'not-allowed' : 'pointer',
                          fontSize: '0.875rem',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (logsCurrentPage !== 1 && selectedFeedLabel) {
                            e.target.style.backgroundColor = '#f3f4f6';
                            e.target.style.borderColor = '#9ca3af';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (logsCurrentPage !== 1 && selectedFeedLabel) {
                            e.target.style.backgroundColor = 'white';
                            e.target.style.borderColor = '#d1d5db';
                          }
                        }}
                      >
                        ← Précédent
                      </button>
                      <span style={{
                        padding: '0.5rem 0.75rem',
                        border: '1px solid #1a1a1a',
                        borderRadius: '0.375rem',
                        backgroundColor: '#FAF9F5',
                        color: '#1d4ed8',
                        fontSize: '0.875rem'
                      }}>
                        Page {logsCurrentPage} sur {Math.ceil(totalLogs / logsPerPage)}
                      </span>
                      <button
                        onClick={() => selectedFeedLabel && loadUpdateLogs(selectedAccount, selectedFeedLabel, logsCurrentPage + 1)}
                        disabled={logsCurrentPage >= Math.ceil(totalLogs / logsPerPage) || !selectedFeedLabel}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.375rem',
                          backgroundColor: logsCurrentPage >= Math.ceil(totalLogs / logsPerPage) ? '#f9fafb' : 'white',
                          color: logsCurrentPage >= Math.ceil(totalLogs / logsPerPage) ? '#9ca3af' : '#374151',
                          cursor: logsCurrentPage >= Math.ceil(totalLogs / logsPerPage) ? 'not-allowed' : 'pointer',
                          fontSize: '0.875rem',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (logsCurrentPage < Math.ceil(totalLogs / logsPerPage) && selectedFeedLabel) {
                            e.target.style.backgroundColor = '#f3f4f6';
                            e.target.style.borderColor = '#9ca3af';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (logsCurrentPage < Math.ceil(totalLogs / logsPerPage) && selectedFeedLabel) {
                            e.target.style.backgroundColor = 'white';
                            e.target.style.borderColor = '#d1d5db';
                          }
                        }}
                      >
                        Suivant →
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p style={{ color: '#6b7280', fontSize: '0.875rem', fontStyle: 'italic' }}>
                Aucune mise à jour effectuée pour le moment
              </p>
            )}
            
            {/* Graphique histogramme CSS custom */}
            {updateLogs.length > 0 && prepareChartData && prepareChartData.length > 0 && (
              <div style={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '0.5rem',
                padding: '1.5rem',
                marginTop: '1.5rem'
              }}>
                <h4 style={{ 
                  fontWeight: '600', 
                  color: '#374151',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  📊 Évolution des produits par mise à jour
                </h4>
                
                {/* Légende */}
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '1rem',
                  marginBottom: '1rem',
                  fontSize: '0.875rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#059669', borderRadius: '2px' }}></div>
                    <span>TOP</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#1a1a1a', borderRadius: '2px' }}></div>
                    <span>MIDDLE</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#f59e0b', borderRadius: '2px' }}></div>
                    <span>LOW</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#ef4444', borderRadius: '2px' }}></div>
                    <span>ZERO</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#6b7280', borderRadius: '2px' }}></div>
                    <span>ZOMBIE</span>
                  </div>
                </div>
                
                {/* Graphique */}
                <div style={{
                  height: '300px',
                  display: 'flex',
                  alignItems: 'end',
                  gap: '1rem',
                  padding: '20px 0',
                  borderBottom: '2px solid #e2e8f0',
                  borderLeft: '2px solid #e2e8f0',
                  marginLeft: '40px',
                  marginBottom: '60px',
                  position: 'relative'
                }}>
                  {/* Axe Y (labels) */}
                  <div style={{
                    position: 'absolute',
                    left: '-40px',
                    top: '0',
                    bottom: '0',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    color: '#64748b'
                  }}>
                    {(() => {
                      const maxTotal = Math.max(...prepareChartData.map(d => d.total), 1);
                      const steps = 5;
                      const stepValue = Math.ceil(maxTotal / steps);
                      return Array.from({length: steps + 1}, (_, i) => (
                        <div key={i} style={{ height: '1px', display: 'flex', alignItems: 'center' }}>
                          {maxTotal - (i * stepValue)}
                        </div>
                      ));
                    })()}
                  </div>
                  
                  {/* Barres */}
                  {prepareChartData.map((data, index) => {
                    const maxTotal = Math.max(...prepareChartData.map(d => d.total), 1);
                    const totalHeight = 260; // hauteur disponible
                    
                    const getHeight = (value) => Math.max((value / maxTotal) * totalHeight, 2);
                    
                    return (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          flex: 1,
                          position: 'relative',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setChartTooltip({
                            visible: true,
                            data: data,
                            position: {
                              x: rect.left + rect.width / 2,
                              y: rect.top - 10
                            }
                          });
                        }}
                        onMouseLeave={() => {
                          setChartTooltip({ visible: false, data: null, position: { x: 0, y: 0 } });
                        }}
                      >
                        {/* Barre empilée */}
                        <div style={{
                          width: '100%',
                          maxWidth: '40px',
                          display: 'flex',
                          flexDirection: 'column-reverse',
                          height: totalHeight
                        }}>
                          {/* TOP */}
                          {data.TOP > 0 && (
                            <div style={{
                              backgroundColor: '#059669',
                              height: `${getHeight(data.TOP)}px`,
                              transition: 'all 0.3s ease',
                              borderRadius: '2px 2px 0 0'
                            }}></div>
                          )}
                          {/* MIDDLE */}
                          {data.MIDDLE > 0 && (
                            <div style={{
                              backgroundColor: '#1a1a1a',
                              height: `${getHeight(data.MIDDLE)}px`,
                              transition: 'all 0.3s ease'
                            }}></div>
                          )}
                          {/* LOW */}
                          {data.LOW > 0 && (
                            <div style={{
                              backgroundColor: '#f59e0b',
                              height: `${getHeight(data.LOW)}px`,
                              transition: 'all 0.3s ease'
                            }}></div>
                          )}
                          {/* ZERO */}
                          {data.ZERO > 0 && (
                            <div style={{
                              backgroundColor: '#ef4444',
                              height: `${getHeight(data.ZERO)}px`,
                              transition: 'all 0.3s ease'
                            }}></div>
                          )}
                          {/* ZOMBIE */}
                          {data.ZOMBIE > 0 && (
                            <div style={{
                              backgroundColor: '#6b7280',
                              height: `${getHeight(data.ZOMBIE)}px`,
                              transition: 'all 0.3s ease',
                              borderRadius: '0 0 2px 2px'
                            }}></div>
                          )}
                        </div>
                        
                        {/* Label date */}
                        <div style={{
                          position: 'absolute',
                          bottom: '-50px',
                          fontSize: '0.75rem',
                          color: '#64748b',
                          transform: 'rotate(-45deg)',
                          transformOrigin: 'center',
                          whiteSpace: 'nowrap',
                          width: 'auto'
                        }}>
                          {data.date}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div style={{ 
                  fontSize: '0.875rem', 
                  color: '#64748b',
                  textAlign: 'center',
                  marginTop: '1rem'
                }}>
                  Les barres empilées montrent la répartition des produits par catégorie pour chaque mise à jour
                </div>
                
                {/* Tooltip personnalisé */}
                {chartTooltip.visible && chartTooltip.data && (
                  <div
                    style={{
                      position: 'fixed',
                      left: `${chartTooltip.position.x}px`,
                      top: `${chartTooltip.position.y}px`,
                      transform: 'translateX(-50%) translateY(-100%)',
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '0.5rem',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                      zIndex: 1000,
                      minWidth: '200px',
                      pointerEvents: 'none'
                    }}
                  >
                    <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                      {chartTooltip.data.fullDate}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                      Total: {chartTooltip.data.total} produits
                    </div>
                    
                    {/* Segments détaillés */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      {chartTooltip.data.TOP > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <div style={{ width: '8px', height: '8px', backgroundColor: '#059669', borderRadius: '1px' }}></div>
                            <span style={{ color: '#374151' }}>TOP</span>
                          </div>
                          <span style={{ color: '#6b7280' }}>
                            {chartTooltip.data.TOP} ({chartTooltip.data.total > 0 ? ((chartTooltip.data.TOP / chartTooltip.data.total) * 100).toFixed(1) : '0'}%)
                          </span>
                        </div>
                      )}
                      
                      {chartTooltip.data.MIDDLE > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <div style={{ width: '8px', height: '8px', backgroundColor: '#1a1a1a', borderRadius: '1px' }}></div>
                            <span style={{ color: '#374151' }}>MIDDLE</span>
                          </div>
                          <span style={{ color: '#6b7280' }}>
                            {chartTooltip.data.MIDDLE} ({chartTooltip.data.total > 0 ? ((chartTooltip.data.MIDDLE / chartTooltip.data.total) * 100).toFixed(1) : '0'}%)
                          </span>
                        </div>
                      )}
                      
                      {chartTooltip.data.LOW > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <div style={{ width: '8px', height: '8px', backgroundColor: '#f59e0b', borderRadius: '1px' }}></div>
                            <span style={{ color: '#374151' }}>LOW</span>
                          </div>
                          <span style={{ color: '#6b7280' }}>
                            {chartTooltip.data.LOW} ({chartTooltip.data.total > 0 ? ((chartTooltip.data.LOW / chartTooltip.data.total) * 100).toFixed(1) : '0'}%)
                          </span>
                        </div>
                      )}
                      
                      {chartTooltip.data.ZERO > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <div style={{ width: '8px', height: '8px', backgroundColor: '#ef4444', borderRadius: '1px' }}></div>
                            <span style={{ color: '#374151' }}>ZERO</span>
                          </div>
                          <span style={{ color: '#6b7280' }}>
                            {chartTooltip.data.ZERO} ({chartTooltip.data.total > 0 ? ((chartTooltip.data.ZERO / chartTooltip.data.total) * 100).toFixed(1) : '0'}%)
                          </span>
                        </div>
                      )}
                      
                      {chartTooltip.data.ZOMBIE > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <div style={{ width: '8px', height: '8px', backgroundColor: '#6b7280', borderRadius: '1px' }}></div>
                            <span style={{ color: '#374151' }}>ZOMBIE</span>
                          </div>
                          <span style={{ color: '#6b7280' }}>
                            {chartTooltip.data.ZOMBIE} ({chartTooltip.data.total > 0 ? ((chartTooltip.data.ZOMBIE / chartTooltip.data.total) * 100).toFixed(1) : '0'}%)
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Graphique histogramme des mises à jour - temporairement désactivé en prod */}
            {false && updateLogs.length > 0 && prepareChartData && prepareChartData.length > 0 && (
              <div style={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '0.5rem',
                padding: '1.5rem',
                marginTop: '1.5rem'
              }}>
                <h4 style={{ 
                  fontWeight: '600', 
                  color: '#374151',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  📊 Évolution des produits par mise à jour
                </h4>
                
                <div style={{ width: '100%', height: '400px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={prepareChartData || []}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="TOP" name="TOP" stackId="products" fill="#059669" />
                      <Bar dataKey="MIDDLE" name="MIDDLE" stackId="products" fill="#3b82f6" />
                      <Bar dataKey="LOW" name="LOW" stackId="products" fill="#f59e0b" />
                      <Bar dataKey="ZERO" name="ZERO" stackId="products" fill="#ef4444" />
                      <Bar dataKey="ZOMBIE" name="ZOMBIE" stackId="products" fill="#6b7280" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                <div style={{ 
                  marginTop: '1rem', 
                  fontSize: '0.875rem', 
                  color: '#64748b',
                  textAlign: 'center'
                }}>
                  Les barres empilées montrent la répartition des produits par catégorie pour chaque mise à jour
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      )}

      {/* Onglet Étiquetage automatique (groupe de produits) */}
      {activeTab === 'group' && selectedAccount && selectedFeedLabel && (
        <>
          {/* Erreur */}
          {groupError && (
            <div className="error-message">
              {groupError}
            </div>
          )}

          {/* Écran de chargement pendant l'analyse */}
          {analyzing && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '3rem',
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              border: '1px solid #e2e8f0',
              margin: '2rem 0',
              minHeight: '400px'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                border: '4px solid #f3f4f6',
                borderTop: '4px solid #1a1a1a',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '1.5rem'
              }}></div>
              <h3 style={{
                color: '#374151',
                fontSize: '1.25rem',
                fontWeight: '600',
                marginBottom: '0.5rem',
                textAlign: 'center'
              }}>
                🔍 Analyse par groupe en cours...
              </h3>
              <p style={{
                color: '#64748b',
                fontSize: '0.875rem',
                textAlign: 'center',
                maxWidth: '400px',
                lineHeight: '1.5'
              }}>
                Récupération et agrégation des données de performance par item_group_id.
                Cela peut prendre quelques instants.
              </p>
            </div>
          )}

          {/* Résultats */}
          {groupAnalysisData && !showXmlFeeds && (
            <div className="results-section">
              {/* Graphiques de distribution */}
              {(() => {
                const { salesData, budgetData, productsData, conversionValueData } = prepareGroupPieChartData();
                return (salesData.length > 0 || budgetData.length > 0 || productsData.length > 0 || conversionValueData.length > 0) && (
                  <div className="charts-section">
                    <h3>Distribution par catégorie (Groupes)</h3>
                    <div className="charts-container">
                      {productsData.length > 0 && (
                        <div className="chart-wrapper">
                          <h4>Répartition des groupes</h4>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={productsData}
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                dataKey="value"
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                              >
                                {productsData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => [value, 'Groupes']} />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {salesData.length > 0 && (
                        <div className="chart-wrapper">
                          <h4>Répartition des ventes</h4>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={salesData}
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                dataKey="value"
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                              >
                                {salesData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => [value.toFixed(1), 'Conversions']} />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {budgetData.length > 0 && (
                        <div className="chart-wrapper">
                          <h4>Répartition du budget</h4>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={budgetData}
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                dataKey="value"
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                              >
                                {budgetData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => [`€${value.toFixed(2)}`, 'Coût']} />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {conversionValueData.length > 0 && (
                        <div className="chart-wrapper">
                          <h4>Répartition valeur de conversion</h4>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={conversionValueData}
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                dataKey="value"
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                              >
                                {conversionValueData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => [`€${value.toFixed(2)}`, 'Valeur']} />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <h3>Résultats de l'analyse par groupe</h3>

              {/* Info produits sans groupe - cliquable pour ouvrir le modal */}
              {groupAnalysisData.stats?.productsWithoutGroup > 0 && (
                <div
                  onClick={() => setShowProductsWithoutGroupModal(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1rem',
                    backgroundColor: '#fef3c7',
                    borderRadius: '8px',
                    color: '#92400e',
                    fontSize: '0.875rem',
                    marginBottom: '1rem',
                    cursor: 'pointer',
                    border: '1px solid #f59e0b',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fde68a';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fef3c7';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  title="Cliquer pour voir la liste des produits sans groupe"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  <span style={{ fontWeight: '500' }}>
                    {groupAnalysisData.stats.productsWithoutGroup} produit(s) sans item_group_id
                  </span>
                  <span style={{ opacity: 0.7, fontSize: '0.8rem' }}>(cliquer pour voir)</span>
                </div>
              )}

              {/* Statistiques globales */}
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-number">{groupAnalysisData.stats?.totalGroups || 0}</div>
                  <div className="stat-label">Groupes analysés</div>
                </div>
                <div
                  className={`stat-card top clickable ${groupFilter === 'top' ? 'active' : ''}`}
                  onClick={() => handleGroupCategoryClick('top')}
                  title="Cliquer pour filtrer les groupes TOP"
                >
                  <div className="stat-number">{groupAnalysisData.summary?.top?.count || 0}</div>
                  <div className="stat-label">TOP performers</div>
                </div>
                <div
                  className={`stat-card middle clickable ${groupFilter === 'middle' ? 'active' : ''}`}
                  onClick={() => handleGroupCategoryClick('middle')}
                  title="Cliquer pour filtrer les groupes MIDDLE"
                >
                  <div className="stat-number">{groupAnalysisData.summary?.middle?.count || 0}</div>
                  <div className="stat-label">MIDDLE performers</div>
                </div>
                <div
                  className={`stat-card low clickable ${groupFilter === 'low' ? 'active' : ''}`}
                  onClick={() => handleGroupCategoryClick('low')}
                  title="Cliquer pour filtrer les groupes LOW"
                >
                  <div className="stat-number">{groupAnalysisData.summary?.low?.count || 0}</div>
                  <div className="stat-label">LOW performers</div>
                </div>
                <div
                  className={`stat-card zero clickable ${groupFilter === 'zero' ? 'active' : ''}`}
                  onClick={() => handleGroupCategoryClick('zero')}
                  title="Cliquer pour filtrer les groupes ZÉRO vente"
                >
                  <div className="stat-number">{groupAnalysisData.summary?.zero?.count || 0}</div>
                  <div className="stat-label">ZÉRO vente</div>
                </div>
                <div
                  className={`stat-card zombie clickable ${groupFilter === 'zombie' ? 'active' : ''}`}
                  onClick={() => handleGroupCategoryClick('zombie')}
                  title="Cliquer pour filtrer les groupes ZOMBIES"
                >
                  <div className="stat-number">{groupAnalysisData.summary?.zombie?.count || 0}</div>
                  <div className="stat-label">ZOMBIES</div>
                </div>
              </div>

              {/* Tableau des groupes */}
              {groupAnalysisData.groups && groupAnalysisData.groups.length > 0 && (
                <div className="products-preview">
                  {groupFilter && (
                    <div className="filter-indicator">
                      Filtre actif: {groupFilter} ({getFilteredGroupsCount()} sur {groupAnalysisData.groups.length})
                      <button
                        onClick={() => setGroupFilter('')}
                        className="clear-filter-btn"
                        title="Supprimer le filtre"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: '60px' }}>Image</th>
                          <th onClick={() => handleGroupSort('title')} className="sortable">
                            Groupe {groupSortConfig.key === 'title' && (groupSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleGroupSort('productCount')} className="sortable">
                            Nb produits {groupSortConfig.key === 'productCount' && (groupSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleGroupSort('label')} className="sortable">
                            Catégorie {groupSortConfig.key === 'label' && (groupSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleGroupSort('impressions')} className="sortable">
                            Impressions {groupSortConfig.key === 'impressions' && (groupSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleGroupSort('ctr')} className="sortable">
                            CTR {groupSortConfig.key === 'ctr' && (groupSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleGroupSort('clicks')} className="sortable">
                            Clics {groupSortConfig.key === 'clicks' && (groupSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleGroupSort('cpc')} className="sortable">
                            CPC {groupSortConfig.key === 'cpc' && (groupSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleGroupSort('cost')} className="sortable">
                            Coût {groupSortConfig.key === 'cost' && (groupSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleGroupSort('conversionRate')} className="sortable">
                            Taux conv. {groupSortConfig.key === 'conversionRate' && (groupSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleGroupSort('conversions')} className="sortable">
                            Conv. {groupSortConfig.key === 'conversions' && (groupSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleGroupSort('conversionsValue')} className="sortable">
                            Valeur conv. {groupSortConfig.key === 'conversionsValue' && (groupSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleGroupSort('roas')} className="sortable">
                            ROAS {groupSortConfig.key === 'roas' && (groupSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {getSortedAndPaginatedGroups.map((group, index) => (
                          <tr
                            key={group.itemGroupId || index}
                            onClick={() => setSelectedGroupForModal(group)}
                            style={{ cursor: 'pointer' }}
                            title="Cliquer pour voir les produits du groupe"
                          >
                            <td style={{ padding: '4px', textAlign: 'center' }}>
                              {group.imageLink ? (
                                <img
                                  src={adresseDeVignette(group)}
                                  alt={group.title || group.itemGroupId}
                                  loading="lazy"
                                  decoding="async"
                                  fetchPriority="low"
                                  width="50"
                                  height="50"
                                  style={{
                                    width: '50px',
                                    height: '50px',
                                    objectFit: 'contain',
                                    borderRadius: '4px',
                                    backgroundColor: '#f9fafb'
                                  }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div style={{
                                  width: '50px',
                                  height: '50px',
                                  backgroundColor: '#f3f4f6',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#9ca3af',
                                  fontSize: '10px'
                                }}>
                                  N/A
                                </div>
                              )}
                            </td>
                            <td className="product-info">
                              <div className="product-title">{group.title || group.itemGroupId}</div>
                              <div className="product-id">ID: {group.itemGroupId}</div>
                            </td>
                            <td>{group.productCount || 0}</td>
                            <td>
                              <span className={`category-badge ${getCategoryColor(group.label?.toLowerCase())}`}>
                                {getCategoryIcon(group.label?.toLowerCase())}
                                {(group.label || '').toUpperCase()}
                              </span>
                            </td>
                            <td>{group.impressions?.toLocaleString() || 0}</td>
                            <td>{group.impressions > 0 ? ((group.clicks / group.impressions) * 100).toFixed(2) + '%' : '0%'}</td>
                            <td>{group.clicks?.toLocaleString() || 0}</td>
                            <td>€{group.clicks > 0 ? (group.cost / group.clicks).toFixed(2) : '0.00'}</td>
                            <td>€{(group.cost || 0).toFixed(2)}</td>
                            <td>{group.clicks > 0 ? ((group.conversions / group.clicks) * 100).toFixed(2) + '%' : '0%'}</td>
                            <td>{(group.conversions || 0).toFixed(1)}</td>
                            <td>€{(group.conversionsValue || 0).toFixed(2)}</td>
                            <td>{group.roas ? `${group.roas.toFixed(0)}%` : 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Contrôles de pagination */}
                  {groupTotalPages > 1 && (
                    <div className="pagination-controls">
                      <div className="pagination-info">
                        <span>
                          Affichage {(groupCurrentPage - 1) * groupItemsPerPage + 1}-{Math.min(groupCurrentPage * groupItemsPerPage, getFilteredGroupsCount())} sur {getFilteredGroupsCount()} groupes
                        </span>
                      </div>
                      <div className="pagination-buttons">
                        <button
                          onClick={() => setGroupCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={groupCurrentPage === 1}
                          className="pagination-btn"
                        >
                          ← Précédent
                        </button>
                        <span className="page-numbers">
                          Page {groupCurrentPage} sur {groupTotalPages}
                        </span>
                        <button
                          onClick={() => setGroupCurrentPage(prev => Math.min(prev + 1, groupTotalPages))}
                          disabled={groupCurrentPage === groupTotalPages}
                          className="pagination-btn"
                        >
                          Suivant →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Message si aucune donnée de groupe après l'analyse */}
          {!groupAnalysisData && !analyzing && !groupError && hasAutoAnalyzed && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '3rem',
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              border: '1px solid #e2e8f0',
              margin: '2rem 0',
              minHeight: '200px'
            }}>
              <Layers size={48} style={{ marginBottom: '1rem', opacity: 0.5, color: '#6b7280' }} />
              <h3 style={{ color: '#374151', marginBottom: '0.5rem' }}>Aucune donnée de groupe disponible</h3>
              <p style={{ color: '#6b7280', textAlign: 'center', maxWidth: '400px' }}>
                {groupsUnavailableReason === 'NO_MERCHANT_CONNECTION' && (
                  <>Aucune connexion Google Merchant Center active. Connectez votre compte Merchant Center dans les paramètres pour accéder aux données de groupe.</>
                )}
                {groupsUnavailableReason === 'NO_LINKED_MERCHANT' && (
                  <>Aucun compte Merchant Center n'est lié à ce compte Google Ads. Liez votre Merchant Center à Google Ads pour accéder aux données de groupe.</>
                )}
                {groupsUnavailableReason === 'NO_ACCESSIBLE_MERCHANT' && (
                  <>Impossible d'accéder aux données Merchant Center. Vérifiez que votre connexion Merchant Center est valide.</>
                )}
                {groupsUnavailableReason === 'MERCHANT_ERROR' && (
                  <>Erreur lors de la récupération des données Merchant Center. Réessayez ultérieurement.</>
                )}
                {groupsUnavailableReason === 'NO_ITEM_GROUP_IDS' && (
                  <>Vos produits Merchant Center n'ont pas d'identifiant de groupe (item_group_id). Ajoutez cet attribut à votre flux produits.</>
                )}
                {(groupsUnavailableReason === 'NO_GROUPS' || !groupsUnavailableReason) && (
                  <>Vos produits n'ont pas d'identifiant de groupe (item_group_id) dans Merchant Center.</>
                )}
              </p>
            </div>
          )}
        </>
      )}

      {/* Onglet Étiquetage manuel */}
      {activeTab === 'manual' && selectedAccount && selectedFeedLabel && (
        <div className="manual-labeling-section">
          {/* En-tête avec contrôles */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1rem',
            padding: '1rem',
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            border: '1px solid #e2e8f0',
            width: '100%',
            boxSizing: 'border-box',
            /* Une seule rangée : voir le commentaire de la barre intérieure. */
            flexWrap: 'nowrap'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              /* « Flux : » se repliait en deux lignes — « Flux » puis « : » — faute de pouvoir
                 refuser de rétrécir. */
              flexShrink: 0,
              whiteSpace: 'nowrap'
            }}>
              <label style={{ fontWeight: '500', color: '#374151' }}>Flux :</label>
              <span style={{ color: '#1a1a1a', fontWeight: '600' }}>{selectedFeedLabel}</span>
            </div>

            {showCustomDates && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                />
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
            )}

            {/* ⚠️ Une seule rangée, et le rétrécissement est la moitié qui compte.
                `flexWrap: 'wrap'` laissait la barre s'étager : la liste des catégories prenait
                963 px sur une carte de 1 534 — un `<select>` est large comme sa plus longue
                option, et les options sont des hiérarchies entières (« loisirs créatifs >
                peintures décoratives > multi-supports fluide - deco »). Le `minWidth: 200px`
                qu'elle portait ne borne rien : c'est un plancher.
                Trois pièces, et il les faut toutes : `nowrap` interdit l'étage, `minWidth: 0`
                autorise un enfant flex à descendre sous la largeur de son contenu — sans lui,
                `flex` ne change rien (voir la barre de l'Optimiseur de flux) —, et
                `flexShrink: 0` protège ce qui ne doit pas maigrir. */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'nowrap',
              flex: '1 1 0',
              minWidth: 0
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                flexShrink: 0,
                border: '1px solid #d1d5db', 
                borderRadius: '0.375rem', 
                padding: '0.5rem', 
                backgroundColor: (loadingProducts || !products || products.length === 0) ? '#f3f4f6' : 'white',
                width: '160px',
                maxWidth: '160px',
                opacity: (loadingProducts || !products || products.length === 0) ? 0.6 : 1
              }}>
                <Search size={16} style={{ 
                  color: (loadingProducts || !products || products.length === 0) ? '#9ca3af' : 'inherit' 
                }} />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={loadingProducts || !products || products.length === 0}
                  style={{ 
                    border: 'none', 
                    outline: 'none', 
                    marginLeft: '0.5rem',
                    width: '120px',
                    maxWidth: '120px',
                    fontSize: '0.875rem',
                    backgroundColor: 'transparent',
                    color: (loadingProducts || !products || products.length === 0) ? '#9ca3af' : 'inherit',
                    cursor: (loadingProducts || !products || products.length === 0) ? 'not-allowed' : 'text'
                  }}
                />
              </div>

              {/* Dropdown catégorie */}
              {/* Largeur *préférée* de 220 px, qui rétrécit si l'écran l'exige et ne
                  grandit pas si la place abonde — sans quoi la liste reprendrait les
                  963 px de sa plus longue option. */}
              <div className="filter-controls" style={{ flex: '0 1 220px', minWidth: 0 }}>
                <select
                  value={selectedManualCategory || ''}
                  onChange={(e) => setSelectedManualCategory(e.target.value)}
                  className="filter-select"
                  disabled={loadingProducts || !products || products.length === 0}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    backgroundColor: (loadingProducts || !products || products.length === 0) ? '#f3f4f6' : 'white',
                    color: (loadingProducts || !products || products.length === 0) ? '#9ca3af' : 'inherit',
                    cursor: (loadingProducts || !products || products.length === 0) ? 'not-allowed' : 'pointer',
                    width: '100%',
                    minWidth: 0,
                    opacity: (loadingProducts || !products || products.length === 0) ? 0.6 : 1
                  }}
                >
                  <option value="">Toutes les catégories</option>
                  {/* Options dynamiques construites depuis les productTypeL1-L5 des produits */}
                  {(() => {
                    if (!products || products.length === 0) {
                      return <option disabled>Aucun produit chargé</option>;
                    }

                    // Construire tous les niveaux de hiérarchies depuis les productTypeL1-L5
                    const categoryHierarchies = new Set();
                    products.forEach(product => {
                      // Construire tous les niveaux de hiérarchie pour chaque produit
                      const productTypes = [
                        product.productTypeL1,
                        product.productTypeL2,
                        product.productTypeL3,
                        product.productTypeL4,
                        product.productTypeL5
                      ].filter(type => type && type.trim() !== '');
                      
                      // Ajouter chaque niveau de hiérarchie
                      for (let i = 1; i <= productTypes.length; i++) {
                        const hierarchy = productTypes.slice(0, i).join(' > ');
                        categoryHierarchies.add(hierarchy);
                      }
                    });

                    // Convertir en array et trier
                    const categoriesArray = Array.from(categoryHierarchies).sort();
                    
                    if (categoriesArray.length === 0) {
                      return <option disabled>Aucune catégorie trouvée</option>;
                    }
                    
                    return categoriesArray.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ));
                  })()}
                </select>
              </div>

              {/* Dropdown libellés */}
              {/* Largeur *préférée* de 220 px, qui rétrécit si l'écran l'exige et ne
                  grandit pas si la place abonde — sans quoi la liste reprendrait les
                  963 px de sa plus longue option. */}
              <div className="filter-controls" style={{ flex: '0 1 220px', minWidth: 0 }}>
                <select
                  value={selectedManualLabel || ''}
                  onChange={(e) => setSelectedManualLabel(e.target.value)}
                  className="filter-select"
                  disabled={loadingProducts || !products || products.length === 0}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    backgroundColor: (loadingProducts || !products || products.length === 0) ? '#f3f4f6' : 'white',
                    color: (loadingProducts || !products || products.length === 0) ? '#9ca3af' : 'inherit',
                    cursor: (loadingProducts || !products || products.length === 0) ? 'not-allowed' : 'pointer',
                    width: '100%',
                    minWidth: 0,
                    opacity: (loadingProducts || !products || products.length === 0) ? 0.6 : 1
                  }}
                >
                  <option value="">Tous les libellés</option>
                  {/* Options dynamiques construites depuis les customLabel0-4 des produits */}
                  {(() => {
                    if (!products || products.length === 0) {
                      return <option disabled>Aucun produit chargé</option>;
                    }

                    // Construire tous les libellés depuis les customLabel0-4 avec leur origine
                    const allLabelsWithOrigin = new Map();
                    products.forEach(product => {
                      // Ajouter chaque libellé personnalisé non vide avec son index
                      [
                        { label: product.customLabel0, index: 0 },
                        { label: product.customLabel1, index: 1 },
                        { label: product.customLabel2, index: 2 },
                        { label: product.customLabel3, index: 3 },
                        { label: product.customLabel4, index: 4 }
                      ].forEach(({ label, index }) => {
                        if (label && label.trim() !== '') {
                          const trimmedLabel = label.trim();
                          const key = `customLabel${index}: ${trimmedLabel}`;
                          allLabelsWithOrigin.set(trimmedLabel, key);
                        }
                      });
                    });

                    // Convertir en array et trier par l'affichage
                    const labelsArray = Array.from(allLabelsWithOrigin.entries())
                      .map(([value, display]) => ({ value, display }))
                      .sort((a, b) => a.display.localeCompare(b.display));
                    
                    if (labelsArray.length === 0) {
                      return <option disabled>Aucun libellé trouvé</option>;
                    }
                    
                    return labelsArray.map(({ value, display }) => (
                      <option key={value} value={value}>
                        {display}
                      </option>
                    ));
                  })()}
                </select>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                opacity: loadingProducts ? 0.5 : 1
              }}>
                <label style={{ color: loadingProducts ? '#9ca3af' : 'inherit' }}>Période:</label>
                <select
                  value={selectedPeriod}
                  onChange={(e) => {
                    setSelectedPeriod(e.target.value);
                    setShowCustomDates(e.target.value === 'CUSTOM');
                  }}
                  className="period-select"
                  disabled={loadingProducts}
                  style={{
                    cursor: loadingProducts ? 'not-allowed' : 'pointer',
                    backgroundColor: loadingProducts ? '#f3f4f6' : 'white'
                  }}
                >
                  <option value="TODAY">Aujourd'hui</option>
                  <option value="YESTERDAY">Hier</option>
                  <option value="LAST_7_DAYS">7 derniers jours</option>
                  <option value="LAST_14_DAYS">14 derniers jours</option>
                  <option value="LAST_30_DAYS">30 derniers jours</option>
                  <option value="THIS_MONTH">Ce mois</option>
                  <option value="LAST_MONTH">Mois dernier</option>
                  <option value="LAST_60_DAYS">60 derniers jours</option>
                  <option value="LAST_90_DAYS">90 derniers jours</option>
                  <option value="THIS_YEAR">Année en cours</option>
                  <option value="LAST_YEAR">Année dernière</option>
                  <option value="CUSTOM">Personnalisé</option>
                </select>
              </div>

              {/* Le ressort qui pousse les deux boutons à droite. `flex: 1 1 0` et non `flex: 1`
                  tout court : sa base doit valoir zéro pour qu'il s'efface entièrement quand la
                  barre est à l'étroit, au lieu de disputer la place aux filtres. */}
              <div style={{ flex: '1 1 0', minWidth: 0 }} />

              {/* Bouton Gérer les libellés manuels */}
              {/* Consultation seule : renommer ou supprimer une étiquette est un geste d'agence. */}
              {!readOnly && (
              <button
                onClick={() => setShowManageTagsModal(true)}
                disabled={loadingProducts || !selectedFeedLabel || tagSuggestions.size === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  cursor: loadingProducts || !selectedFeedLabel || tagSuggestions.size === 0 ? 'not-allowed' : 'pointer',
                  opacity: loadingProducts || !selectedFeedLabel || tagSuggestions.size === 0 ? 0.5 : 1,
                  flexShrink: 0
                }}
              >
                <Tags size={16} />
                Libellés ({tagSuggestions.size})
              </button>
              )}

              {/* ⚠️ Un seul bouton à cette place, et il change de rôle selon la sélection.
                  « Libeller N produits » vivait au milieu de la barre, entre les filtres : il y
                  séparait les deux listes de leurs voisines et apparaissait loin du geste qui
                  l'appelle. Les deux actions sont des gestes de masse — l'une sur les lignes
                  cochées, l'autre sur une liste d'identifiants collée —, et la seconde n'a plus
                  d'objet quand on tient déjà une sélection. Elle lui cède donc la place, au lieu
                  de s'ajouter à côté. */}
              {/* Consultation seule : les gestes de masse sont des gestes d'agence. */}
              {!readOnly && (selectedProducts.size > 0 ? (
                <button
                  onClick={() => {
                    setShowTagModal(true);
                    // Afficher les suggestions si disponibles
                    if (tagSuggestions.size > 0) {
                      setTimeout(() => setShowTagSuggestions(true), 100);
                    }
                  }}
                  style={{ ...STYLE_BOUTON_PRINCIPAL, cursor: 'pointer' }}
                >
                  <Tags size={16} />
                  Libeller {selectedProducts.size} produit{selectedProducts.size > 1 ? 's' : ''}
                </button>
              ) : (
                <button
                  onClick={() => setShowBulkAssignModal(true)}
                  disabled={loadingProducts || !selectedFeedLabel}
                  style={{
                    ...STYLE_BOUTON_PRINCIPAL,
                    cursor: loadingProducts || !selectedFeedLabel ? 'not-allowed' : 'pointer',
                    opacity: loadingProducts || !selectedFeedLabel ? 0.6 : 1
                  }}
                >
                  <Tag size={16} />
                  Assigner en masse
                </button>
              ))}
            </div>
          </div>

          {/* Tableau des produits */}
          {loadingProducts ? (
            <div className="loading-products">
              <div className="spinner"></div>
              <p>Chargement des produits...</p>
            </div>
          ) : products.length > 0 ? (
            <div className="products-grille-conteneur">
              {/* `products-grille`, et jamais `products-table` : voir ToolsView.css —
                  la règle générique `table[class*="-table"]` d'App.css capture
                  la seconde par sous-chaîne et écrase toutes les largeurs. */}
              <table className="products-grille">
                <thead>
                  <tr>
                    <th className="select-column">
                      <input
                        type="checkbox"
                        disabled={readOnly}
                        checked={selectedProducts.size === paginatedProducts.length && paginatedProducts.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProducts(new Set(paginatedProducts.map(p => p.productId)));
                          } else {
                            setSelectedProducts(new Set());
                          }
                        }}
                      />
                    </th>
                    <th className="image-column">Image</th>
                    <th onClick={() => handleSort('title')} className="sortable">
                      Titre {sortConfig.key === 'title' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('productId')} className="sortable">
                      Product ID {sortConfig.key === 'productId' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('category')} className="sortable">
                      Catégorie {sortConfig.key === 'category' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('customLabel0')} className="sortable">
                      Libellé {sortConfig.key === 'customLabel0' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('clicks')} className="sortable">
                      Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('cost')} className="sortable">
                      Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('conversions')} className="sortable">
                      Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('conversionsValue')} className="sortable">
                      Valeur conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('cos')} className="sortable">
                      COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('performanceLabel')} className="sortable">
                      Libellé auto {sortConfig.key === 'performanceLabel' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('manualTag')} className="sortable">
                      Libellé manuel {sortConfig.key === 'manualTag' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedProducts.map(product => (
                      <tr key={product.productId}>
                        <td>
                          <input
                            type="checkbox"
                            disabled={readOnly}
                            checked={selectedProducts.has(product.productId)}
                            onChange={(e) => {
                              const newSelected = new Set(selectedProducts);
                              if (e.target.checked) {
                                newSelected.add(product.productId);
                              } else {
                                newSelected.delete(product.productId);
                              }
                              setSelectedProducts(newSelected);
                            }}
                          />
                        </td>
                        <td className="image-column">
                          {product.imageUrl ? (
                            product.merchantCenterUrl ? (
                              <a 
                                href={product.merchantCenterUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                title="Cliquer pour voir dans Google Merchant Center"
                                style={{ 
                                  display: 'inline-block',
                                  borderRadius: '4px'
                                }}
                                onClick={() => {
                                  console.log('🔗 Clic sur lien GMC:', product.merchantCenterUrl);
                                }}
                              >
                                <img 
                                  src={adresseDeVignette(product)} 
                                  alt={product.title}
                                  className="product-image"
                                  loading="lazy"
                                  decoding="async"
                                  fetchPriority="low"
                                  width="60"
                                  height="60"
                                  style={{ 
                                    width: '60px', 
                                    height: '60px', 
                                    objectFit: 'cover', 
                                    cursor: 'pointer',
                                    display: 'block'
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                                <div 
                                  style={{ 
                                    width: '60px', 
                                    height: '60px', 
                                    backgroundColor: '#f3f4f6',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '4px',
                                    display: 'none',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    color: '#6b7280',
                                    cursor: 'pointer'
                                  }}
                                >
                                  📷
                                </div>
                              </a>
                            ) : (
                              <div style={{ position: 'relative' }}>
                                <img 
                                  src={adresseDeVignette(product)} 
                                  alt={product.title}
                                  className="product-image"
                                  loading="lazy"
                                  decoding="async"
                                  fetchPriority="low"
                                  width="60"
                                  height="60"
                                  style={{ 
                                    width: '60px', 
                                    height: '60px', 
                                    objectFit: 'cover'
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                                <div 
                                  style={{ 
                                    width: '60px', 
                                    height: '60px', 
                                    backgroundColor: '#f3f4f6',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '4px',
                                    display: 'none',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    color: '#6b7280'
                                  }}
                                >
                                  📷
                                </div>
                              </div>
                            )
                          ) : (
                            product.merchantCenterUrl ? (
                              <a 
                                href={product.merchantCenterUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                title="Cliquer pour voir dans Google Merchant Center"
                                style={{ 
                                  display: 'inline-block',
                                  borderRadius: '4px'
                                }}
                                onClick={() => {
                                  console.log('🔗 Clic sur lien GMC (sans image):', product.merchantCenterUrl);
                                }}
                              >
                                <div className="no-image">Pas d'image</div>
                              </a>
                            ) : (
                              <div className="no-image">Pas d'image</div>
                            )
                          )}
                        </td>
                        <td className="product-title">
                          {product.title}
                        </td>
                        <td className="product-id">{product.productId}</td>
                        <td className="product-category">
                          {(() => {
                            // Construire les types de produits depuis les segments Google Ads
                            const productTypes = [
                              product.productTypeL1,
                              product.productTypeL2,
                              product.productTypeL3,
                              product.productTypeL4,
                              product.productTypeL5
                            ].filter(type => type && type.trim() !== '');
                            
                            return productTypes.length > 0 
                              ? productTypes.join(' > ')
                              : 'Aucune catégorie';
                          })()}
                        </td>
                        <td className="product-custom-label">
                          {(() => {
                            // Rassembler tous les libellés custom (0 à 4)
                            const labels = [
                              product.customLabel0,
                              product.customLabel1,
                              product.customLabel2,
                              product.customLabel3,
                              product.customLabel4
                            ].filter(label => label && label.trim() !== '');
                            
                            return labels.length > 0 
                              ? labels.join(' | ')
                              : '-';
                          })()}
                        </td>
                        <td>{product.clicks}</td>
                        <td>{product.cost.toFixed(2)}€</td>
                        <td>{product.conversions.toFixed(1)}</td>
                        <td>{product.conversionsValue.toFixed(2)}€</td>
                        <td>{product.cos.toFixed(0)}%</td>
                        <td>
                          <span className={`label-badge label-${product.label.toLowerCase()}`}>
                            {product.label}
                          </span>
                        </td>
                        <td>
                          {editingProductId === product.productId ? (
                            <div className="tag-edit">
                              <div className="tag-input-container">
                                <input
                                  type="text"
                                  value={editingTag}
                                  onChange={handleEditTagChange}
                                  onFocus={() => {
                                    // Afficher les suggestions au focus si disponibles
                                    if (tagSuggestions.size > 0) {
                                      setShowEditTagSuggestions(true);
                                    }
                                  }}
                                  onBlur={() => setTimeout(() => setShowEditTagSuggestions(false), 200)}
                                  className="tag-input"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      applyManualTag(product.productId, editingTag);
                                      setEditingProductId(null);
                                      setEditingTag('');
                                    } else if (e.key === 'Escape') {
                                      setEditingProductId(null);
                                      setEditingTag('');
                                    }
                                  }}
                                  autoFocus
                                />
                                {showEditTagSuggestions && tagSuggestions.size > 0 && (
                                  <div className="tag-suggestions">
                                    {Array.from(tagSuggestions)
                                      .slice(0, 5)
                                      .map(suggestion => (
                                        <div
                                          key={suggestion}
                                          className="tag-suggestion-item"
                                          style={{
                                            background: getTagColor(suggestion).background,
                                            color: getTagColor(suggestion).color,
                                          }}
                                          onClick={() => {
                                            // Appliquer immédiatement le tag suggéré
                                            applyManualTag(product.productId, suggestion);
                                            setEditingProductId(null);
                                            setEditingTag('');
                                            setShowEditTagSuggestions(false);
                                          }}
                                        >
                                          {suggestion}
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => {
                                  applyManualTag(product.productId, editingTag);
                                  setEditingProductId(null);
                                  setEditingTag('');
                                }}
                                className="btn-save-tag"
                              >
                                <Save size={12} />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingProductId(null);
                                  setEditingTag('');
                                }}
                                className="btn-cancel-tag"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ) : (
                            <div className="tag-display">
                              {product.manualTag ? (
                                <>
                                  <span 
                                    className="manual-tag-badge"
                                    style={{
                                      background: getTagColor(product.manualTag).background,
                                      color: getTagColor(product.manualTag).color,
                                    }}
                                  >
                                    {product.manualTag}
                                  </span>
                                  {/* Consultation seule : l'étiquette se lit, elle ne se retouche pas. */}
                                  {!readOnly && (
                                  <>
                                  <button
                                    onClick={() => {
                                      setEditingProductId(product.productId);
                                      setEditingTag(product.manualTag);
                                    }}
                                    className="btn-edit-tag"
                                  >
                                    <Edit3 size={12} />
                                  </button>
                                  <button
                                    onClick={() => removeManualTag(product.productId)}
                                    className="btn-remove-tag"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                  </>
                                  )}
                                </>
                                  ) : !readOnly ? (
                                <button
                                  onClick={() => {
                                    setEditingProductId(product.productId);
                                    setEditingTag('');
                                  }}
                                  className="btn-add-tag"
                                >
                                  <Tag size={12} />
                                  Ajouter un libellé
                                </button>
                                  ) : null}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {/* Pagination */}
              {displayProducts.length > manualItemsPerPage && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  borderTop: '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc'
                }}>
                  <span style={{ color: '#64748b', fontSize: '0.875rem' }}>
                    Affichage {(manualCurrentPage - 1) * manualItemsPerPage + 1}-{Math.min(manualCurrentPage * manualItemsPerPage, displayProducts.length)} sur {displayProducts.length} produits
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => setManualCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={manualCurrentPage === 1}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        backgroundColor: manualCurrentPage === 1 ? '#f3f4f6' : 'white',
                        cursor: manualCurrentPage === 1 ? 'not-allowed' : 'pointer',
                        opacity: manualCurrentPage === 1 ? 0.5 : 1
                      }}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                      Page {manualCurrentPage} / {manualTotalPages}
                    </span>
                    <button
                      onClick={() => setManualCurrentPage(prev => Math.min(manualTotalPages, prev + 1))}
                      disabled={manualCurrentPage === manualTotalPages}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        backgroundColor: manualCurrentPage === manualTotalPages ? '#f3f4f6' : 'white',
                        cursor: manualCurrentPage === manualTotalPages ? 'not-allowed' : 'pointer',
                        opacity: manualCurrentPage === manualTotalPages ? 0.5 : 1
                      }}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="no-products">
              <p>Aucun produit trouvé pour la période sélectionnée.</p>
            </div>
          )}

          {/* Modal pour étiquetage en lot.
              ⚠️ Classes `etq-`, et non les communs `modal-*` : voir ToolsView.css — sept
              déclarations de `.modal-content` se surchargeaient partiellement, et un
              `padding: 24px` venu d'App.css encartait le filet du titre et le fond du pied.
              ⚠️ Et par un portail, comme l'impose le CLAUDE.md et comme le fait déjà
              `BulkAssignModal` : posée dans l'arbre, elle dépend du `z-index` de ses ancêtres. */}
          {showTagModal && createPortal(
            <div className="etq-modale-fond" onClick={() => setShowTagModal(false)}>
              <div className="etq-modale" onClick={(e) => e.stopPropagation()}>
                <div className="etq-modale-entete">
                  <h3>Appliquer un libellé à {selectedProducts.size} produits</h3>
                  <button onClick={() => setShowTagModal(false)} className="etq-modale-fermer" aria-label="Fermer">
                    <X size={16} />
                  </button>
                </div>
                <div className="etq-modale-corps">
                  <label>Libellé à appliquer :</label>
                  <div className="tag-input-container">
                    <input
                      type="text"
                      value={bulkTag}
                      onChange={handleBulkTagChange}
                      onFocus={() => {
                        // Afficher les suggestions au focus si disponibles
                        if (tagSuggestions.size > 0) {
                          setShowTagSuggestions(true);
                        }
                      }}
                      onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                      className="etq-modale-champ"
                      placeholder={tagSuggestions.size > 0 ? "Entrez le libellé... (cliquez pour voir les suggestions)" : "Entrez le libellé..."}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          applyBulkTag();
                        }
                      }}
                    />
                    {showTagSuggestions && tagSuggestions.size > 0 && (
                      <div style={{marginTop: '8px'}}>
                        <label style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '8px', display: 'block'}}>
                          Libellés suggérés (cliquez pour sélectionner) :
                        </label>
                        <div className="tag-suggestions">
                          {Array.from(tagSuggestions)
                            .slice(0, 10)
                            .map(suggestion => (
                              <div
                                key={suggestion}
                                className="tag-suggestion-item"
                                style={{
                                  background: getTagColor(suggestion).background,
                                  color: getTagColor(suggestion).color,
                                }}
                                onClick={() => {
                                  setBulkTag(suggestion);
                                  setShowTagSuggestions(false);
                                }}
                              >
                                {suggestion}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="etq-modale-pied">
                  <button onClick={() => setShowTagModal(false)} className="etq-modale-annuler">
                    Annuler
                  </button>
                  <button onClick={applyBulkTag} className="etq-modale-valider" disabled={!bulkTag.trim()}>
                    Appliquer le libellé
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
      )}

      {/* Modal Assigner en masse par IDs (composant memoïsé) */}
      <BulkAssignModal
        isOpen={showBulkAssignModal}
        onClose={closeBulkAssignModal}
        productIds={bulkProductIds}
        setProductIds={setBulkProductIds}
        tag={bulkAssignTag}
        setTag={setBulkAssignTag}
        loading={bulkAssignLoading}
        onSubmit={handleBulkAssignByIds}
        suggestions={tagSuggestions}
      />

      {/* Modal Gérer les tags */}
      {showManageTagsModal && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={closeManageTagsModal}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '450px',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #e2e8f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>Gérer les libellés manuels</h3>
              <button
                onClick={closeManageTagsModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px'
                }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              {Array.from(tagSuggestions).length === 0 ? (
                <p style={{ color: '#64748b', textAlign: 'center' }}>Aucun libellé manuel</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {Array.from(tagSuggestions).sort().map((tag) => (
                    <div
                      key={tag}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 12px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      {editingTagName === tag ? (
                        <>
                          <input
                            type="text"
                            value={newTagName}
                            onChange={(e) => setNewTagName(e.target.value)}
                            placeholder="Nouveau nom"
                            autoFocus
                            style={{
                              flex: 1,
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db',
                              fontSize: '0.875rem'
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleRenameTag(tag, newTagName);
                              } else if (e.key === 'Escape') {
                                setEditingTagName(null);
                                setNewTagName('');
                              }
                            }}
                          />
                          <button
                            onClick={() => handleRenameTag(tag, newTagName)}
                            disabled={manageTagsLoading || !newTagName.trim()}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              border: 'none',
                              backgroundColor: '#10b981',
                              color: 'white',
                              cursor: manageTagsLoading || !newTagName.trim() ? 'not-allowed' : 'pointer',
                              opacity: manageTagsLoading || !newTagName.trim() ? 0.5 : 1,
                              fontSize: '0.8rem'
                            }}
                          >
                            OK
                          </button>
                          <button
                            onClick={() => {
                              setEditingTagName(null);
                              setNewTagName('');
                            }}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }}
                          >
                            Annuler
                          </button>
                        </>
                      ) : (
                        <>
                          <span style={{ flex: 1, fontWeight: '500' }}>{tag}</span>
                          <button
                            onClick={() => {
                              setEditingTagName(tag);
                              setNewTagName(tag);
                            }}
                            disabled={manageTagsLoading}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db',
                              backgroundColor: 'white',
                              cursor: manageTagsLoading ? 'not-allowed' : 'pointer',
                              fontSize: '0.8rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <Edit3 size={14} />
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDeleteTag(tag)}
                            disabled={manageTagsLoading}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              border: 'none',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              cursor: manageTagsLoading ? 'not-allowed' : 'pointer',
                              opacity: manageTagsLoading ? 0.5 : 1,
                              fontSize: '0.8rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <Trash2 size={14} />
                            Supprimer
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #e2e8f0',
              backgroundColor: '#f8fafc',
              borderBottomLeftRadius: '12px',
              borderBottomRightRadius: '12px'
            }}>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
                La suppression d'un libellé le retire immédiatement de tous les produits.
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Tooltip temporairement désactivé pour debug */}
      {false && hoveredImage && (
        <div>Tooltip désactivé</div>
      )}

      {/* Modal produits sans groupe */}
      {showProductsWithoutGroupModal && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowProductsWithoutGroupModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '900px',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
                  Produits sans item_group_id ({groupAnalysisData?.stats?.productsWithoutGroup || 0})
                </h3>
              </div>
              <button
                onClick={() => setShowProductsWithoutGroupModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Info */}
            <div style={{
              padding: '12px 20px',
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              color: '#64748b'
            }}>
              Ces produits n'ont pas d'identifiant de groupe (item_group_id) dans Merchant Center et ne sont donc pas inclus dans l'analyse par groupe.
            </div>

            {/* Table */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '0'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.875rem'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid #e2e8f0', width: '60px' }}>Image</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>ID Produit</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>Titre</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>Impressions</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>Clics</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>Coût</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>Conv.</th>
                  </tr>
                </thead>
                <tbody>
                  {groupAnalysisData?.stats?.productsWithoutGroupList?.map((product, index) => (
                    <tr
                      key={product.id || index}
                      style={{
                        borderBottom: '1px solid #e2e8f0',
                        backgroundColor: index % 2 === 0 ? 'white' : '#f8fafc'
                      }}
                    >
                      <td style={{ padding: '8px 16px' }}>
                        {product.imageUrl ? (
                          <img
                            src={adresseDeVignette(product)}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            fetchPriority="low"
                            width="40"
                            height="40"
                            style={{
                              width: '40px',
                              height: '40px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              border: '1px solid #e2e8f0'
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: '#f1f5f9',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#94a3b8',
                            fontSize: '0.7rem'
                          }}>
                            N/A
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '8px 16px', fontFamily: 'monospace', fontSize: '0.8rem', color: '#64748b' }}>
                        {product.id}
                      </td>
                      <td style={{ padding: '8px 16px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {product.title || '-'}
                      </td>
                      <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                        {(product.impressions || 0).toLocaleString('fr-FR')}
                      </td>
                      <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                        {(product.clicks || 0).toLocaleString('fr-FR')}
                      </td>
                      <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                        {(product.cost || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                      </td>
                      <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                        {(product.conversions || 0).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!groupAnalysisData?.stats?.productsWithoutGroupList || groupAnalysisData.stats.productsWithoutGroupList.length === 0) && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  Aucun produit sans groupe à afficher
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '12px 20px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowProductsWithoutGroupModal(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal détails d'un groupe de produits */}
      {selectedGroupForModal && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setSelectedGroupForModal(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '1400px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              margin: '20px'
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {selectedGroupForModal.imageLink && (
                  <img
                    src={adresseDeVignette(selectedGroupForModal)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    width="48"
                    height="48"
                    style={{
                      width: '48px',
                      height: '48px',
                      objectFit: 'contain',
                      borderRadius: '6px',
                      backgroundColor: '#f9fafb'
                    }}
                  />
                )}
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
                    {selectedGroupForModal.title || selectedGroupForModal.itemGroupId}
                  </h3>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                    ID groupe: {selectedGroupForModal.itemGroupId} • {selectedGroupForModal.productCount || 0} produit(s)
                  </div>
                </div>
                <span className={`category-badge ${getCategoryColor(selectedGroupForModal.label?.toLowerCase())}`} style={{ marginLeft: '0.5rem' }}>
                  {getCategoryIcon(selectedGroupForModal.label?.toLowerCase())}
                  {(selectedGroupForModal.label || '').toUpperCase()}
                </span>
              </div>
              <button
                onClick={() => setSelectedGroupForModal(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Résumé des performances du groupe */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
              gap: '12px',
              padding: '16px 20px',
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1e293b' }}>
                  {(selectedGroupForModal.impressions || 0).toLocaleString('fr-FR')}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Impressions</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1e293b' }}>
                  {selectedGroupForModal.impressions > 0 ? ((selectedGroupForModal.clicks / selectedGroupForModal.impressions) * 100).toFixed(2) : '0'}%
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>CTR</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1e293b' }}>
                  {(selectedGroupForModal.clicks || 0).toLocaleString('fr-FR')}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Clics</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1e293b' }}>
                  {selectedGroupForModal.clicks > 0 ? (selectedGroupForModal.cost / selectedGroupForModal.clicks).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' }) : '0,00 €'}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>CPC</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1e293b' }}>
                  {(selectedGroupForModal.cost || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Coût</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1e293b' }}>
                  {selectedGroupForModal.clicks > 0 ? ((selectedGroupForModal.conversions / selectedGroupForModal.clicks) * 100).toFixed(2) : '0'}%
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Taux conv.</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1e293b' }}>
                  {(selectedGroupForModal.conversions || 0).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Conv.</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1e293b' }}>
                  {(selectedGroupForModal.conversionsValue || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Valeur conv.</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: '600', color: selectedGroupForModal.roas >= 100 ? '#16a34a' : '#dc2626' }}>
                  {selectedGroupForModal.roas ? `${selectedGroupForModal.roas.toFixed(0)}%` : 'N/A'}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>ROAS</div>
              </div>
            </div>

            {/* Tableau des produits */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '0'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.875rem'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid #e2e8f0', width: '60px' }}>Image</th>
                    <th
                      onClick={() => setGroupProductsSortConfig(prev => ({ key: 'title', direction: prev.key === 'title' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                      style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' }}
                    >
                      Produit {groupProductsSortConfig.key === 'title' && (groupProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th
                      onClick={() => setGroupProductsSortConfig(prev => ({ key: 'category', direction: prev.key === 'category' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                      style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' }}
                    >
                      Catégorie {groupProductsSortConfig.key === 'category' && (groupProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th
                      onClick={() => setGroupProductsSortConfig(prev => ({ key: 'impressions', direction: prev.key === 'impressions' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                      style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' }}
                    >
                      Impressions {groupProductsSortConfig.key === 'impressions' && (groupProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th
                      onClick={() => setGroupProductsSortConfig(prev => ({ key: 'ctr', direction: prev.key === 'ctr' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                      style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' }}
                    >
                      CTR {groupProductsSortConfig.key === 'ctr' && (groupProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th
                      onClick={() => setGroupProductsSortConfig(prev => ({ key: 'clicks', direction: prev.key === 'clicks' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                      style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' }}
                    >
                      Clics {groupProductsSortConfig.key === 'clicks' && (groupProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th
                      onClick={() => setGroupProductsSortConfig(prev => ({ key: 'cpc', direction: prev.key === 'cpc' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                      style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' }}
                    >
                      CPC {groupProductsSortConfig.key === 'cpc' && (groupProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th
                      onClick={() => setGroupProductsSortConfig(prev => ({ key: 'cost', direction: prev.key === 'cost' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                      style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' }}
                    >
                      Coût {groupProductsSortConfig.key === 'cost' && (groupProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th
                      onClick={() => setGroupProductsSortConfig(prev => ({ key: 'convRate', direction: prev.key === 'convRate' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                      style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' }}
                    >
                      Taux conv. {groupProductsSortConfig.key === 'convRate' && (groupProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th
                      onClick={() => setGroupProductsSortConfig(prev => ({ key: 'conversions', direction: prev.key === 'conversions' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                      style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' }}
                    >
                      Conv. {groupProductsSortConfig.key === 'conversions' && (groupProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th
                      onClick={() => setGroupProductsSortConfig(prev => ({ key: 'conversionsValue', direction: prev.key === 'conversionsValue' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                      style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' }}
                    >
                      Valeur conv. {groupProductsSortConfig.key === 'conversionsValue' && (groupProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th
                      onClick={() => setGroupProductsSortConfig(prev => ({ key: 'roas', direction: prev.key === 'roas' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                      style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' }}
                    >
                      ROAS {groupProductsSortConfig.key === 'roas' && (groupProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Trier les produits
                    const sortedProducts = [...(selectedGroupForModal.products || [])].map(product => ({
                      ...product,
                      ctr: product.impressions > 0 ? (product.clicks / product.impressions) * 100 : 0,
                      cpc: product.clicks > 0 ? product.cost / product.clicks : 0,
                      convRate: product.clicks > 0 ? (product.conversions / product.clicks) * 100 : 0,
                      roas: product.cost > 0 ? (product.conversionsValue / product.cost) * 100 : 0
                    })).sort((a, b) => {
                      const key = groupProductsSortConfig.key;
                      const direction = groupProductsSortConfig.direction === 'asc' ? 1 : -1;

                      if (key === 'title' || key === 'category') {
                        const aVal = (a[key] || '').toLowerCase();
                        const bVal = (b[key] || '').toLowerCase();
                        return aVal.localeCompare(bVal) * direction;
                      }

                      const aVal = a[key] || 0;
                      const bVal = b[key] || 0;
                      return (aVal - bVal) * direction;
                    });

                    return sortedProducts.map((product, index) => {
                    const impressions = product.impressions || 0;
                    const clicks = product.clicks || 0;
                    const cost = product.cost || 0;
                    const conversions = product.conversions || 0;
                    const conversionsValue = product.conversionsValue || 0;
                    const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
                    const cpc = clicks > 0 ? cost / clicks : 0;
                    const convRate = clicks > 0 ? (conversions / clicks) * 100 : 0;
                    const roas = cost > 0 ? (conversionsValue / cost) * 100 : 0;

                    return (
                      <tr
                        key={product.id || product.productId || index}
                        style={{
                          borderBottom: '1px solid #e2e8f0',
                          backgroundColor: index % 2 === 0 ? 'white' : '#f8fafc'
                        }}
                      >
                        <td style={{ padding: '8px 16px' }}>
                          {product.imageUrl ? (
                            <img
                              src={adresseDeVignette(product)}
                              alt=""
                              loading="lazy"
                              decoding="async"
                              fetchPriority="low"
                              width="40"
                              height="40"
                              style={{
                                width: '40px',
                                height: '40px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid #e2e8f0'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '40px',
                              height: '40px',
                              backgroundColor: '#f1f5f9',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#94a3b8',
                              fontSize: '0.7rem'
                            }}>
                              N/A
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '8px 16px' }}>
                          <div style={{ fontWeight: '500', maxWidth: '280px', lineHeight: '1.3' }}>
                            {product.title || '-'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b', fontFamily: 'monospace' }}>
                            {product.id || product.productId}
                          </div>
                        </td>
                        <td style={{ padding: '8px 16px' }}>
                          <span className={`category-badge ${getCategoryColor(product.category?.toLowerCase())}`}>
                            {getCategoryIcon(product.category?.toLowerCase())}
                            {(product.category || '').toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                          {impressions.toLocaleString('fr-FR')}
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                          {ctr.toFixed(2)}%
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                          {clicks.toLocaleString('fr-FR')}
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                          {cpc.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                          {cost.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                          {convRate.toFixed(2)}%
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                          {conversions.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                          {conversionsValue.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'right', color: roas >= 100 ? '#16a34a' : '#dc2626' }}>
                          {roas.toFixed(0)}%
                        </td>
                      </tr>
                    );
                    });
                  })()}
                </tbody>
              </table>
              {(!selectedGroupForModal.products || selectedGroupForModal.products.length === 0) && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  Aucun produit à afficher dans ce groupe
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '12px 20px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setSelectedGroupForModal(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Messages d'erreur */}
      {error && (
        <div className="error-message" style={{ margin: '1rem 0' }}>
          <X size={16} />
          {error}
          <button onClick={() => setError('')} className="btn-close-error">
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductLabelingTool;